import { LOADING_STATUSES } from "@constants";
import { numeroOuPadrao } from "@utils/formatters";
import {
  Activity,
  BarChart3,
  CheckCircle2,
  ChevronDown,
  ClipboardList,
  Crosshair,
  Download,
  Mail,
  MapPin,
  NotebookPen,
  Phone,
  RouteIcon,
  Share2,
  Sparkles,
  TrendingUp,
  Trophy,
  UserCheck,
  Users,
  X,
} from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import Logo from "../../assets/img/logo.png";
import Overview from "../overview";
import {
  buildRespondentsByPeriod,
  buildRespondentsMonthlyChartData,
  buildValoresAtuaisModal,
  calcGanhoSugerido,
  computeDashboardLoadingState,
} from "./dashboardHelpers";

// Shared tokens / helpers / sub-components (sliced out of v45 monolith)
import * as S from "./_shared";

// 10 sliced views (one per tab)
import ActionPlanView from "../tabActionPlanView";
import BenchmarkingView from "../tabBenchmarkingView";
import CooperacaoView from "../tabCooperacaoView";
import DriversView from "../tabDriversView";
import EvaView from "../tabEvaView";
import ExecutiveView from "../tabExecutiveView";
import JourneyView from "../tabJourneyView";
import SegmentationView from "../tabSegmentationView";
import TrendsView from "../tabTrendsView";
import VerbatinsView from "../tabVerbatinsView";

import { firebaseAuth } from "../../config/firebase";
import AuthService from "../../services/Auth/auth.service";
import { endSession, trackEvent } from "../../services/usageTracking";
import { exportDashboardToXlsx } from "../../services/xlsxExportService";
import { showError } from "../../utils/alerts";

import { useDispatch, useSelector } from "react-redux";

import PDFExportModal from "../pdfExportModal";
import PeriodPillBig from "../periodPillBig";
import {
  buildSelectedPer,
  formatPeriodLabel,
} from "../periodPillBig/periodPillBig.helpers.js";
import Tag from "../tag";
import LoadingState from "../ui/LoadingState";

import { dashboardService } from "../../services/dashboardService.js";
import {
  createActionPlan,
  deleteActionPlan,
  selectActionPlansItems,
  updateActionPlan,
} from "../../store/slices/actionPlansSlice";
import {
  fetchCurrentUser,
  selectCompanyName,
  selectIsSuperadmin,
  selectUserName,
} from "../../store/slices/authSlice";
import {
  fetchAllDashboard,
  fetchFilteredDashboard,
  loadDashboardForPeriod,
  selectRespondentsMonthly,
} from "../../store/slices/dashboardSlice";
import {
  setCity,
  setClientType,
  setNpsSegment,
  setPeriod,
} from "../../store/slices/filtersSlice";
import Card from "../card";
import Section from "../section";

import NotificationCenter from "../notification";
import NotificationService from "../../services/Notifications/notifications.service.jsx";
import { useNotifications } from "../../hooks/useNotifications.js";
import { PrintModeContext } from "../charts/printMode";
import { describeActiveFilters } from "../pdfExportModal/pdfFilters";

// Pull every shared export into local scope so the Dashboard body (state hooks,
// modals, TopBar, TabNav, NPSAnchor, etc.) can use them unqualified.
const {
  AcaoModal,
  C,
  F,
  getAchievementStats,
  JCFG,
  markTabVisited,
  markWelcomed,
  NPSAnchor,
  SHORT,
  TT,
  UNIMED,
  etSt,
  moe,
  todayStr,
} = S;

const PERIODS = ["mensal", "trimestral", "semestral", "anual"];

const PERIOD_TYPE_BY_KEY = {
  mensal: "monthly",
  trimestral: "quarterly",
  semestral: "semiannual",
  anual: "annual",
};

export default function Dashboard() {
  const navigate = useNavigate();
  const [periodoG, setPeriodoG] = useState("mensal");
  const companyName = useSelector(selectCompanyName) || UNIMED;
  const loggedUserName = useSelector(selectUserName);
  const isSuperadmin = useSelector(selectIsSuperadmin);
  const [cidadesAtivas, setCidadesAtivas] = useState([]);
  const [tipoBeneficiario, setTipoBeneficiario] = useState("todos"); // "todos" | "proprio" | "intercambio"
  const [tipoBenefAberto, setTipoBenefAberto] = useState(false);
  const [filtroCidadeAberto, setFiltroCidadeAberto] = useState(false);
  const [filtroNpsAberto, setFiltroNpsAberto] = useState(false);
  const [filtroVinculoAberto, setFiltroVinculoAberto] = useState(false);
  const [npsSegmentoAtivo, setNpsSegmentoAtivo] = useState("");
  const [vinculoAtivo, setVinculoAtivo] = useState("");
  const {
    notifications,
    setNotifications,
    isLoading: notificationsLoading,
    counts: notificationCounts,
    refreshCounts: refreshNotificationCounts,
    loadMore: loadMoreNotifications,
  } = useNotifications();

  const NPS_OPCOES = [
    {
      label: "Detratores Críticos (0 a 4)",
      value: "Detratores Críticos (0 a 4)",
    },
    { label: "Detratores (0 a 6)", value: "Detratores (0 a 6)" },
    { label: "Neutros (7 e 8)", value: "Neutros (7 e 8)" },
    { label: "Promotores (9 e 10)", value: "Promotores (9 e 10)" },
  ];
  const VINCULO_OPCOES = [
    {
      label: "Próprios da Unimed",
      value: "Próprios da Unimed",
      apiValue: "Local",
    },
    { label: "Intercâmbio", value: "Intercâmbio", apiValue: "Intercâmbio" },
  ];

  const dispatchAllFetches = () => dispatch(fetchFilteredDashboard());

  const toggleCidade = (c) => {
    const isActive = cidadesAtivas.includes(c);
    const next = isActive ? [] : [c];
    setCidadesAtivas(next);
    dispatch(setCity(next.length === 0 ? "todas" : next[0]));
    setFiltroCidadeAberto(false);
    dispatchAllFetches();
    trackEvent("filter_applied", "city", { value: next[0] ?? "todas" });
  };
  const todasCidades = cidadesAtivas.length === 0;
  const nenhumaCidade = cidadesAtivas.length === 0;
  const [periodoCustom, setPeriodoCustom] = useState({ inicio: "", fim: "" });

  const [metas, setMetas] = useState({ nps: 70, csat: 79, ces: 66, jcx: 73 });
  const [editandoMeta, setEditandoMeta] = useState(null);
  // Metas individuais das 11 etapas — inicializadas a partir de JCFG
  const [metasEtapas, setMetasEtapas] = useState(() => {
    const init = {};
    Object.keys(JCFG).forEach((k) => {
      init[k] = JCFG[k].meta;
    });
    return init;
  });
  const [editandoMetaEt, setEditandoMetaEt] = useState(null);
  // Minimizar card inteiro de metas
  const [metasCardOpen, setMetasCardOpen] = useState(true);
  // Toggle de gamificação no card de Sucessos (on = full com níveis/badges, off = modo simples corporativo)
  const [gamificacaoOn, setGamificacaoOn] = useState(true);
  // Feed cooperativo Sistema Unimed
  const [coopPlanos, setCoopPlanos] = useState([]);
  const [coopBusca, setCoopBusca] = useState("");
  const [coopEtapaFilter, setCoopEtapaFilter] = useState("todas");
  const [coopCurtidas, setCoopCurtidas] = useState(() => new Set());
  const [coopDriverFilter, setCoopDriverFilter] = useState(() => new Set());
  const [coopComentariosAbertos, setCoopComentariosAbertos] = useState(
    () => new Set(),
  );
  const [coopNovoComentario, setCoopNovoComentario] = useState({});
  // Wizard de conclusão automática (trigger: status → concluido sem sucesso registrado)
  const [concluirWizard, setConcluirWizard] = useState(null);
  // Bulk adjust de metas (% sobre o atual)
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkPct, setBulkPct] = useState("");
  const [bulkApplyGerais, setBulkApplyGerais] = useState(true);
  const [bulkApplyEtapas, setBulkApplyEtapas] = useState(true);
  // Helper: atualiza state E muta JCFG (pra propagar pra todas as referências sem refatorar)
  const setMetaEtapa = (nome, valor) => {
    JCFG[nome].meta = valor;
    setMetasEtapas((prev) => ({ ...prev, [nome]: valor }));
  };
  const [tab, setTab] = useState("executivo");
  const handleTabChange = (newTab) => {
    setTab(newTab);
  };
  // Conquistas de sessão: primeiro acesso (uma vez) e aba visitada (a cada
  // troca — inclusive quando alguma view chama ctx.setTab direto, sem passar
  // por handleTabChange, ex.: atalhos "Ver ações"/"Perguntar à Eva").
  useEffect(() => {
    markWelcomed();
  }, []);
  useEffect(() => {
    markTabVisited(tab);
    trackEvent("tab_view", tab);
  }, [tab]);
  const [etapaSel, setEtapaSel] = useState(null);
  const [acoes, setAcoes] = useState([]);
  const [acaoSel, setAcaoSel] = useState(null);
  const [hiddenLines, setHiddenLines] = useState({});
  const toggleLine = (key) =>
    setHiddenLines((prev) => ({ ...prev, [key]: !prev[key] }));
  const [filtroSt, setFiltroSt] = useState(() => new Set());
  const [filtroResp, setFiltroResp] = useState(() => new Set());
  const [stDropOpen, setStDropOpen] = useState(false);
  const [respDropOpen, setRespDropOpen] = useState(false);
  const [busca, setBusca] = useState("");
  const [modal, setModal] = useState(null);
  const [pJ, setPJ] = useState("trimestral");
  const [pT, setPT] = useState("trimestral");
  const [agrupT, setAgrupT] = useState("mes"); // granularidade da aba Tendências: mes/tri/sem/ano
  const [pS, setPS] = useState("trimestral");
  const [pE, setPE] = useState("trimestral");
  const [pD, setPD] = useState("trimestral");
  const [engajamentoModalOpen, setEngajamentoModalOpen] = useState(false);
  const [prefilledAcao, setPrefilledAcao] = useState(null);
  const [etapaDriversFilter, setEtapaDriversFilter] = useState(null); // null = Geral, ou nome da etapa
  const [etapaHistSel, setEtapaHistSel] = useState("Atend. Operadora"); // etapa para o gráfico de evolução por etapa
  const [driverSortMode, setDriverSortMode] = useState("volume"); // "volume" | "pior" | "melhor"
  const [segIndicador, setSegIndicador] = useState("nps"); // "nps" | "csat" | "ces" — qual indicador a aba Segmentação mostra
  const [segHistMode, setSegHistMode] = useState({}); // toggle de série histórica por chart (key: chartId, value: bool)
  const toggleSegHist = (key) =>
    setSegHistMode((prev) => ({ ...prev, [key]: !prev[key] }));
  const [pB, setPB] = useState("trimestral");
  const [printPages, setPrintPages] = useState(null);
  const [printPeriodo, setPrintPeriodo] = useState(null);
  const [exportingXlsx, setExportingXlsx] = useState(false);

  const handleLogout = async () => {
    await endSession();
    const { message, type } = await AuthService.signOut();
    type === "success" ? navigate("/login") : showError(message);
  };

  // Período da tela antes do PDF, quando o modal escolheu outro — restaurado
  // no afterprint (ver useEffect do cleanup).
  const periodoDaTelaAntesDoPdf = useRef(null);

  async function handleGeneratePDF({ pages, periodo }) {
    setModal(null);
    trackEvent("action", "pdf_exported");
    const periodoPdf = periodo === "custom" ? period : periodo;
    // Os números das páginas vêm do store: sem recarregar o período
    // escolhido, só capa e rodapé mudavam e os dados continuavam os da tela.
    if (periodoPdf && periodoPdf !== period) {
      periodoDaTelaAntesDoPdf.current = period;
      await dispatch(loadDashboardForPeriod(periodoPdf));
    }
    setPrintPeriodo(periodoPdf);
    setPrintPages(pages);
    setTimeout(() => window.print(), 600);
  }

  async function handleExportXlsx() {
    if (exportingXlsx) return;
    setExportingXlsx(true);
    try {
      await exportDashboardToXlsx(filters, companyName);
      trackEvent("action", "xlsx_exported");
    } catch (error) {
      console.error("Erro ao exportar XLSX:", error);
      showError(
        error?.message ||
          "Não foi possível exportar os dados. Tente novamente mais tarde.",
      );
    } finally {
      setExportingXlsx(false);
    }
  }

  const dispatch = useDispatch();
  const dashboardState = useSelector((state) => state.dashboard);
  const filters = useSelector((state) => state.filters);
  const period = useSelector((state) => state.filters.period);
  const storedPeriods = useSelector((state) => state.filters.periods);
  // Linhas cruas de /periods (com end_date) — base do rótulo e das ondas do
  // período selecionado (buildSelectedPer).
  const periodRows = useSelector(
    (state) => state.dashboard.periods?.data?.data || [],
  );
  const activePeriodStartDate = storedPeriods.find(
    (p) =>
      p.name === period &&
      (!p.city || p.city === "") &&
      (!p.nps_segment || p.nps_segment === "") &&
      (!p.client_type || p.client_type === ""),
  )?.start_date;
  const cidades = [
    ...new Set(
      storedPeriods
        .filter(
          (p) =>
            p.city &&
            p.city !== "" &&
            activePeriodStartDate &&
            p.start_date === activePeriodStartDate,
        )
        .map((p) => p.city),
    ),
  ].sort((a, b) => {
    const aIsOutras = a.toLowerCase() === "outras cidades";
    const bIsOutras = b.toLowerCase() === "outras cidades";
    if (aIsOutras) return 1;
    if (bIsOutras) return -1;
    return 0;
  });
  const apItems = useSelector(selectActionPlansItems);
  const hasInitializedDashboard = useRef(false);

  useEffect(() => {
    if (!hasInitializedDashboard.current) {
      hasInitializedDashboard.current = true;
      dispatch(fetchCurrentUser());
      dispatch(fetchAllDashboard());
    }
  }, [dispatch]);

  useEffect(() => {
    const setupNotifications = async () => {
      if (!("serviceWorker" in navigator)) {
        return;
      }

      await navigator.serviceWorker.register("/firebase-messaging-sw.js");

      if (!("Notification" in window)) {
        return;
      }

      if (Notification.permission === "denied") {
        return;
      }

      if (Notification.permission === "default") {
        const permission = await Notification.requestPermission();
        if (permission !== "granted") {
          return;
        }
      }

      // Cadastra o aparelho a cada abertura: se o token do Firebase mudou ou
      // venceu, a API atualiza o registro e reativa o aparelho.
      try {
        const userToken = await firebaseAuth.currentUser?.getIdToken();
        await NotificationService.registerDeviceToken(userToken);
      } catch (error) {
        console.error("Erro ao cadastrar aparelho para push:", error);
      }
    };

    setupNotifications();
  }, []);

  const overviewState = useSelector((state) => state.dashboard.overview);
  const cooperacaoState = useSelector((state) => state.dashboard.cooperacao);
  const overview = overviewState?.data?.data || [[{ respondents: 1 }]];
  const journeyForModal =
    useSelector((state) => state.dashboard.journey)?.data?.data || [];

  useEffect(() => {
    const cooperacaoItems = cooperacaoState?.data?.data;
    if (Array.isArray(cooperacaoItems)) {
      setCoopPlanos(cooperacaoItems);
    }
  }, [cooperacaoState?.data?.data]);
  const valoresAtuaisModal = buildValoresAtuaisModal(
    overviewState?.data?.data?.[0] || {},
    journeyForModal,
  );
  const respondentsMonthlyState = useSelector(selectRespondentsMonthly);
  const respondentsMonthlyRows = respondentsMonthlyState?.data?.data || [];
  const respondentsMonthlyChartData = buildRespondentsMonthlyChartData(
    respondentsMonthlyRows,
    PERIOD_TYPE_BY_KEY.mensal,
  );
  // Trimestral/semestral/anual não têm carga própria em respondents_monthly
  // (só existem linhas "monthly" hoje) — agregamos a partir dos meses reais
  // mais recentes em vez de usar os rótulos fixos de PER.
  const respondentsByPeriod = buildRespondentsByPeriod({
    periods: PERIODS,
    respondentsMonthlyRows,
    formatPeriodLabel,
    monthlyType: PERIOD_TYPE_BY_KEY.mensal,
  });

  const requiredDashboardKeys = [
    "periods",
    "benchmarking",
    "chats",
    "overview",
    "drivers",
    "driverEssence",
    "driverMetrics",
    "driverMetricsMonthly",
    "driverMonthly",
    "insights",
    "journey",
    "respondentsMonthly",
    "segmentation",
    "cooperacao",
  ];

  const { isInitialLoading, dashboardError } = computeDashboardLoadingState(
    dashboardState,
    requiredDashboardKeys,
    LOADING_STATUSES.IDLE,
  );

  const dashboardLoading = isInitialLoading;

  useEffect(() => {
    setPJ(periodoG);
    setPT(periodoG);
    setPS(periodoG);
    setPE(periodoG);
    setPD(periodoG);
    setPB(periodoG);
  }, [periodoG]);

  useEffect(() => {
    const cleanup = () => {
      setPrintPages(null);
      setPrintPeriodo(null);
      const periodoDaTela = periodoDaTelaAntesDoPdf.current;
      if (periodoDaTela) {
        periodoDaTelaAntesDoPdf.current = null;
        dispatch(loadDashboardForPeriod(periodoDaTela));
      }
    };
    window.addEventListener("afterprint", cleanup);
    return () => window.removeEventListener("afterprint", cleanup);
  }, []);

  if (dashboardLoading) {
    return (
      <div
        id="dashboard-root"
        style={{
          fontFamily: F.text,
          background: C.bg,
          minHeight: "100vh",
          color: C.text,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: 24,
        }}
      >
        <LoadingState message="Carregando dashboard..." />
      </div>
    );
  }

  if (dashboardError) {
    return (
      <div
        id="dashboard-root"
        style={{
          fontFamily: F.text,
          background: C.bg,
          minHeight: "100vh",
          color: C.text,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: 24,
          textAlign: "center",
        }}
      >
        <div style={{ maxWidth: 520 }}>
          <h1 style={{ marginBottom: 12 }}>Erro ao carregar o dashboard</h1>
          <p style={{ color: C.text4, marginBottom: 20 }}>{dashboardError}</p>
          <button
            type="button"
            onClick={() => dispatch(fetchAllDashboard())}
            style={{
              border: "none",
              borderRadius: 999,
              padding: "10px 18px",
              background: C.g700,
              color: "white",
              cursor: "pointer",
              fontWeight: 700,
            }}
          >
            Recarregar
          </button>
        </div>
      </div>
    );
  }

  async function saveAcao(a) {
    // Plans from the DB have _apiId set by fromApiItem; mock/new plans do not
    const isNova = !a._apiId;
    const old = isNova
      ? acoes.find((x) => x.id === a.id)
      : apItems.find((x) => x.id === a._apiId);
    const isTransicaoConcluido =
      old &&
      old.status !== "concluido" &&
      a.status === "concluido" &&
      !a.sucesso;

    let updateOk = true;
    if (isNova) {
      dispatch(createActionPlan(a));
      trackEvent("action", "action_plan_created");
    } else {
      try {
        await dispatch(updateActionPlan(a)).unwrap();
      } catch (err) {
        updateOk = false;
        alert(`Erro ao salvar plano de ação:\n${err}`);
      }
    }

    if (isTransicaoConcluido && updateOk) {
      // Usa o valor atual do dashboard (live) para calcular a melhora real
      const valorDash =
        valoresAtuaisModal[a.etapa] ?? numeroOuPadrao(a.v_atual, 0);
      const ganhoAuto = Math.round(
        valorDash - numeroOuPadrao(a.v_baseline, valorDash),
      );
      const acaoComValorAtual = { ...a, v_atual: Math.round(valorDash) };
      setConcluirWizard({
        step: "registrar_sucesso",
        acao: acaoComValorAtual,
        ganhoAuto,
        ganhoEdit: String(Math.max(0, ganhoAuto)),
        obs: "",
        autorPub:
          a.resp ||
          loggedUserName ||
          firebaseAuth.currentUser?.displayName ||
          firebaseAuth.currentUser?.email ||
          "—",
        tituloPub: a.titulo,
        descPub: a.desc || "",
        telefonePub: "",
        emailPub: "",
      });
    }
  }

  function delAcao(id) {
    setAcoes((prev) => prev.filter((x) => x.id !== id));
    dispatch(deleteActionPlan(id));
    if (acaoSel === id) setAcaoSel(null);
  }

  // Compartilha um plano concluído no feed CooperAção Unimed
  async function compartilharPlano(a, opts = {}) {
    if (!a) return;
    const payload = {
      unimed: UNIMED,
      uf: "SP",
      autor:
        opts.autor ||
        loggedUserName ||
        firebaseAuth.currentUser?.displayName ||
        firebaseAuth.currentUser?.email ||
        "—",
      avatar: "🌱",
      verif: true,
      titulo: opts.titulo || a.titulo,
      desc: opts.desc || a.desc || "Plano compartilhado pela Unimed.",
      etapa: a.etapa,
      drivers: a.drivers || [],
      ganhoPp: a.sucesso ? a.sucesso.ganhoPp || 0 : opts.ganhoPp || 0,
      duracaoDias: (a.check || []).length * 15 || 60,
      numTarefas: (a.check || []).length,
      responsaveis: [a.resp].filter(Boolean),
      tags: ["novo", "campinas"],
      telefone: opts.telefone || null,
      email: opts.email || null,
    };

    let novoPost = null;
    try {
      novoPost = await dashboardService.publishToCooperacao(payload);
    } catch (error) {
      console.error("Erro ao publicar CooperAção:", error);
      showError(
        error?.message ||
          "Não foi possível publicar no CooperAção. Tente novamente mais tarde.",
      );
      return;
    }

    if (!novoPost) {
      showError("Não foi possível publicar no CooperAção. Tente novamente.");
      return;
    }

    trackEvent("action", "cooperacao_published", { postId: novoPost.id });
    setCoopPlanos((prev) => [novoPost, ...prev]);
    // setNotifications((prev) => [
    //   {
    //     id: Date.now(),
    //     tipo: "coop_publish",
    //     titulo: "🤝 Plano publicado no CooperAção Unimed",
    //     corpo: `"${novoPost.titulo}" agora está visível pra todas as Unimeds filiadas. Outras cooperativas já podem curtir, comentar e reusar. Ganho reportado: +${
    //       payload.etapa === "NPS Geral" ? " pts" : "pp"
    //     } em ${novoPost.etapa}.`,
    //     quando: "agora",
    //     lida: false,
    //     coopPostId: novoPost.id,
    //   },
    //   ...prev,
    // ]);
    handleTabChange("coop");
    if (!opts.silent) {
      alert(
        "✅ Plano publicado com sucesso no CooperAção Unimed!\n\nOutras Unimeds já podem ver, comentar e reusar seu plano. Obrigado por apoiar o Sistema Unimed.",
      );
    }
  }

  const toggleCurtida = async (id) => {
    if (coopCurtidas.has(id)) return;
    try {
      await dashboardService.likeCooperacao(id);
      trackEvent("action", "cooperacao_liked", { postId: id });
      setCoopCurtidas((prev) => new Set(prev).add(id));
      setCoopPlanos((prev) =>
        prev.map((p) =>
          p.id === id ? { ...p, curtidas: (p.curtidas || 0) + 1 } : p,
        ),
      );
    } catch (error) {
      console.error("Erro ao curtir post CooperAção:", error);
      showError(
        error?.message ||
          "Não foi possível registrar sua curtida. Tente novamente mais tarde.",
      );
    }
  };

  const toggleComentarios = (id) =>
    setCoopComentariosAbertos((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const adicionarComentario = async (planoId) => {
    const texto = (coopNovoComentario[planoId] || "").trim();
    if (!texto) return;
    const novoCom = {
      unimed: UNIMED,
      autor:
        loggedUserName ||
        firebaseAuth.currentUser?.displayName ||
        firebaseAuth.currentUser?.email ||
        "—",
      avatar: "🌱",
      texto,
      dt: todayStr(),
    };

    try {
      const saved = await dashboardService.commentOnCooperacao(
        planoId,
        novoCom,
      );
      trackEvent("action", "cooperacao_commented", { postId: planoId });
      setCoopPlanos((prev) =>
        prev.map((p) =>
          p.id === planoId
            ? {
                ...p,
                comentariosLista: [...(p.comentariosLista || []), saved],
                comentarios: (p.comentarios || 0) + 1,
              }
            : p,
        ),
      );
      setCoopNovoComentario((prev) => ({ ...prev, [planoId]: "" }));
    } catch (error) {
      console.error("Erro ao comentar no CooperAção:", error);
      showError(
        error?.message ||
          "Não foi possível enviar o comentário. Tente novamente mais tarde.",
      );
    }
  };

  const toggleCoopDriver = (slug) =>
    setCoopDriverFilter((prev) => {
      const next = new Set(prev);
      if (next.has(slug)) next.delete(slug);
      else next.add(slug);
      return next;
    });

  // Abre o wizard de publicação já no step "publicar" a partir de uma ação com sucesso registrado.
  // Usado quando o usuário quer publicar uma ação já concluída (ou quando clica "Registrar + Publicar" no form).
  function openPublishWizard(acao) {
    if (!acao) return;
    const ganho = calcGanhoSugerido(acao);
    setConcluirWizard({
      step: "publicar",
      acao,
      ganhoAuto: ganho,
      ganhoEdit: String(ganho),
      obs: acao.sucesso?.observacao || "",
      autorPub:
        acao.resp ||
        loggedUserName ||
        firebaseAuth.currentUser?.displayName ||
        firebaseAuth.currentUser?.email ||
        "—",
      tituloPub: acao.titulo,
      descPub: acao.desc || "",
      telefonePub: "",
      emailPub: "",
    });
  }

  // Rótulo do período selecionado a partir dos períodos reais da API — a
  // constante PER (_shared.jsx) tinha rótulos fixos e fazia a tela mostrar
  // "Snapshot do período — Março 2026" em qualquer período.
  const per = buildSelectedPer(
    periodRows,
    period,
    overview[0]?.respondents ?? null,
  );

  // Tabs do relatório (sequência principal) e tabs operacionais (fora do relatório)
  const TABS = [
    { id: "executivo", label: "Visão Geral", Icon: BarChart3 },
    { id: "jornada", label: "Jornada", Icon: RouteIcon },
    { id: "drivers", label: "Drivers", Icon: Crosshair },
    { id: "tendencias", label: "Tendências", Icon: TrendingUp },
    { id: "segmentos", label: "Segmentação", Icon: Users },
    { id: "eva", label: "Eva & IA", Icon: Sparkles },
    { id: "benchmark", label: "Benchmarking", Icon: Trophy },
    { id: "quest", label: "Metodologia", Icon: NotebookPen },
  ];
  const TABS_OPS = [
    { id: "acoes", label: "Plano de Ação", Icon: ClipboardList },
    { id: "coop", label: "CooperAção Unimed", Icon: Share2 },
  ];

  // Single ctx object passed to each sliced view. Over-destructured keys are
  // harmless (resolve to undefined if not present).
  const ctx = {
    periodoG,
    setPeriodoG,
    periodoCustom,
    setPeriodoCustom,
    cidadesAtivas,
    setCidadesAtivas,
    toggleCidade,
    tipoBeneficiario,
    setTipoBeneficiario,
    tipoBenefAberto,
    setTipoBenefAberto,
    todasCidades,
    nenhumaCidade,
    filtroCidadeAberto,
    setFiltroCidadeAberto,
    tab,
    setTab,
    per,
    metas,
    setMetas,
    editandoMeta,
    setEditandoMeta,
    metasEtapas,
    setMetasEtapas,
    editandoMetaEt,
    setEditandoMetaEt,
    metasCardOpen,
    setMetasCardOpen,
    setMetaEtapa,
    bulkOpen,
    setBulkOpen,
    bulkPct,
    setBulkPct,
    bulkApplyGerais,
    setBulkApplyGerais,
    bulkApplyEtapas,
    setBulkApplyEtapas,
    gamificacaoOn,
    setGamificacaoOn,
    acoes,
    setAcoes,
    acaoSel,
    setAcaoSel,
    concluirWizard,
    setConcluirWizard,
    etapaSel,
    setEtapaSel,
    hiddenLines,
    setHiddenLines,
    toggleLine,
    // per-tab UI state
    pE,
    setPE,
    pJ,
    setPJ,
    pD,
    setPD,
    pS,
    setPS,
    pB,
    setPB,
    agrupT,
    setAgrupT,
    driverSortMode,
    setDriverSortMode,
    etapaDriversFilter,
    setEtapaDriversFilter,
    etapaHistSel,
    setEtapaHistSel,
    segIndicador,
    setSegIndicador,
    segHistMode,
    toggleSegHist,
    filtroSt,
    setFiltroSt,
    filtroResp,
    setFiltroResp,
    busca,
    setBusca,
    respDropOpen,
    setRespDropOpen,
    stDropOpen,
    setStDropOpen,
    setPrefilledAcao,
    // action-plan handlers
    delAcao,
    compartilharPlano,
    openPublishWizard,
    // coop handlers
    toggleCurtida,
    toggleComentarios,
    toggleCoopDriver,
    adicionarComentario,
    // derived values
    coopPlanos,
    setCoopPlanos,
    coopBusca,
    setCoopBusca,
    coopEtapaFilter,
    setCoopEtapaFilter,
    coopCurtidas,
    setCoopCurtidas,
    coopDriverFilter,
    setCoopDriverFilter,
    coopComentariosAbertos,
    setCoopComentariosAbertos,
    coopNovoComentario,
    setCoopNovoComentario,
    modal,
    setModal,
    notifications,
    setNotifications,
    notificationsLoading,
    notificationCounts,
    refreshNotificationCounts,
    loadMoreNotifications,
  };

  const btnStyle = (active) => ({
    display: "flex",
    alignItems: "center",
    gap: 5,
    background: active ? C.surface : "transparent",
    color: active ? C.text : C.text3,
    border: "none",
    borderRadius: 8,
    padding: "5px 12px",
    fontSize: 12,
    fontWeight: active ? 600 : 400,
    cursor: "pointer",
    transition: "all .15s",
    boxShadow: active ? "0 1px 3px rgba(0,0,0,.08)" : "none",
    whiteSpace: "nowrap",
  });

  return (
    <div
      id="dashboard-root"
      style={{
        fontFamily: F.text,
        background: C.bg,
        minHeight: "100vh",
        color: C.text,
      }}
    >
      {/* Topbar */}
      <div
        className="no-print"
        style={{
          background: "rgba(255,255,255,.82)",
          backdropFilter: "blur(20px)",
          WebkitBackdropFilter: "blur(20px)",
          borderBottom: `1px solid ${C.border}`,
          padding: "0 24px",
          display: "flex",
          alignItems: "center",
          gap: 14,
          height: 52,
          position: "sticky",
          top: 0,
          zIndex: 100,
        }}
      >
        <img
          src={Logo}
          alt="121"
          style={{
            width: 32,
            height: 32,
            borderRadius: "50%",
            objectFit: "cover",
            flexShrink: 0,
          }}
        />
        <div>
          <div
            style={{
              fontWeight: 700,
              fontSize: 15,
              letterSpacing: "-0.03em",
              lineHeight: 1,
              color: C.text,
            }}
          >
            121 Insights
          </div>
          <div
            style={{
              fontSize: 9,
              color: C.text4,
              fontWeight: 500,
              letterSpacing: "0.02em",
            }}
          >
            by 121Labs
          </div>
        </div>
        <div style={{ width: 1, height: 20, background: C.border }} />
        <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
          <div
            style={{
              width: 7,
              height: 7,
              borderRadius: "50%",
              background: C.green,
            }}
          />
          <span style={{ fontSize: 13, fontWeight: 500, color: C.text2 }}>
            {companyName}
          </span>
        </div>
        <div
          style={{ width: 1, height: 20, background: C.border, flexShrink: 0 }}
        />
        <span
          style={{
            fontSize: 12,
            color: C.text4,
            flexShrink: 1,
            minWidth: 0,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          Jornada de Experiência do Beneficiário
        </span>
        <div style={{ flex: 1 }} />
        <button
          onClick={() => setModal("pdf")}
          title="Exportar PDF"
          style={{
            display: "flex",
            alignItems: "center",
            gap: 5,
            background: C.surf3,
            border: `1px solid ${C.border}`,
            borderRadius: 10,
            padding: "6px 12px",
            cursor: "pointer",
            fontSize: 12,
            fontWeight: 500,
            color: C.text2,
            transition: "all .15s",
          }}
        >
          <Download size={13} /> Exportar PDF
        </button>
        <button
          onClick={handleExportXlsx}
          disabled={exportingXlsx}
          title="Exportar dados quantitativos em XLSX"
          style={{
            display: "flex",
            alignItems: "center",
            gap: 5,
            background: C.surf3,
            border: `1px solid ${C.border}`,
            borderRadius: 10,
            padding: "6px 12px",
            cursor: exportingXlsx ? "wait" : "pointer",
            fontSize: 12,
            fontWeight: 500,
            color: C.text2,
            opacity: exportingXlsx ? 0.6 : 1,
            transition: "all .15s",
          }}
        >
          <Download size={13} />{" "}
          {exportingXlsx ? "Exportando…" : "Exportar XLSX"}
        </button>

        {/* Badge de engajamento amostral — clica e abre modal */}
        <button
          onClick={() => setEngajamentoModalOpen(true)}
          title="Clique para ver detalhes da amostra"
          style={{
            display: "flex",
            alignItems: "center",
            gap: 6,
            background: C.surf3,
            border: `1px solid ${C.border}`,
            borderRadius: 10,
            padding: "6px 12px",
            cursor: "pointer",
            fontSize: 12,
            fontWeight: 500,
            color: C.text2,
            transition: "all .15s",
            fontFamily: "inherit",
          }}
        >
          <Activity
            size={13}
            color={moe(overview[0]?.respondents) <= 10 ? C.g700 : C.orange}
          />
          <span>
            n=
            <b style={{ color: C.text }}>
              {overview[0]?.respondents?.toLocaleString()}
            </b>
          </span>
          <span style={{ color: C.text4 }}>·</span>
          <span
            style={{
              color: moe(overview[0]?.respondents) <= 10 ? C.g700 : C.orange,
              fontWeight: 600,
            }}
          >
            ±{moe(overview[0]?.respondents)}%
          </span>
        </button>

        {/* Filtro global de cidade */}
        <div style={{ position: "relative" }}>
          <button
            onClick={() => setFiltroCidadeAberto((o) => !o)}
            title="Filtrar por cidade"
            style={{
              display: "flex",
              alignItems: "center",
              gap: 6,
              maxWidth: 280,
              background: filtroCidadeAberto
                ? C.greenBg
                : todasCidades
                  ? C.surf3
                  : C.greenBg,
              border: `1px solid ${
                filtroCidadeAberto || !todasCidades ? C.g700 : C.border
              }`,
              borderRadius: 10,
              padding: "6px 12px",
              cursor: "pointer",
              fontSize: 12,
              fontWeight: 500,
              color: filtroCidadeAberto || !todasCidades ? C.g700 : C.text2,
              transition: "all .15s",
              fontFamily: "inherit",
            }}
          >
            <MapPin size={13} style={{ flexShrink: 0 }} />
            <span
              style={{
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
                minWidth: 0,
              }}
            >
              {todasCidades ? "Todas as cidades" : cidadesAtivas[0]}
            </span>
            <ChevronDown
              size={12}
              style={{
                flexShrink: 0,
                transform: filtroCidadeAberto ? "rotate(180deg)" : "none",
                transition: "transform .15s",
              }}
            />
          </button>
          {filtroCidadeAberto && (
            <>
              <div
                onClick={() => setFiltroCidadeAberto(false)}
                style={{ position: "fixed", inset: 0, zIndex: 199 }}
              />
              <div
                style={{
                  position: "absolute",
                  top: "calc(100% + 8px)",
                  right: 0,
                  minWidth: 240,
                  background: C.surface,
                  border: `1px solid ${C.border}`,
                  borderRadius: 12,
                  boxShadow: "0 12px 32px rgba(0,0,0,.16)",
                  zIndex: 200,
                  overflow: "hidden",
                }}
              >
                <div
                  style={{
                    padding: "12px 14px",
                    borderBottom: `1px solid ${C.border}`,
                    background: C.surf3,
                  }}
                >
                  <div
                    style={{
                      fontSize: 11,
                      fontWeight: 700,
                      color: C.text,
                      textTransform: "uppercase",
                      letterSpacing: "0.05em",
                    }}
                  >
                    Filtrar por cidade
                  </div>
                </div>
                <div
                  style={{
                    maxHeight: 280,
                    overflowY: "auto",
                    padding: "6px 0",
                  }}
                >
                  {[
                    { label: "Todas as cidades", value: null },
                    ...cidades.map((c) => ({
                      label:
                        c.toLowerCase() === "outras cidades"
                          ? "Outras Cidades"
                          : c,
                      value: c,
                    })),
                  ].map(({ label, value }) => {
                    const active =
                      value === null
                        ? todasCidades
                        : cidadesAtivas.includes(value);
                    const handleClick = () => {
                      if (value === null) {
                        setCidadesAtivas([]);
                        dispatch(setCity("todas"));
                        setFiltroCidadeAberto(false);
                        dispatchAllFetches();
                      } else {
                        toggleCidade(value);
                      }
                    };
                    return (
                      <div
                        key={label}
                        onClick={handleClick}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 10,
                          padding: "9px 14px",
                          cursor: "pointer",
                          background: active ? `${C.greenBg}` : "transparent",
                          transition: "background .12s",
                        }}
                        onMouseEnter={(e) => {
                          if (!active)
                            e.currentTarget.style.background = C.surf3;
                        }}
                        onMouseLeave={(e) => {
                          if (!active)
                            e.currentTarget.style.background = "transparent";
                        }}
                      >
                        <div
                          style={{
                            width: 16,
                            height: 16,
                            borderRadius: "50%",
                            border: `1.5px solid ${active ? C.g700 : C.border}`,
                            background: active ? C.g700 : "#FFFFFF",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            flexShrink: 0,
                          }}
                        >
                          {active && (
                            <div
                              style={{
                                width: 6,
                                height: 6,
                                borderRadius: "50%",
                                background: "#FFFFFF",
                              }}
                            />
                          )}
                        </div>
                        <span
                          style={{
                            fontSize: 12,
                            color: active ? C.g700 : C.text2,
                            fontWeight: active ? 600 : 500,
                          }}
                        >
                          {label}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>
            </>
          )}
        </div>

        {/* Filtro NPS Segmento */}
        <div style={{ position: "relative" }}>
          <button
            onClick={() => {
              setFiltroNpsAberto((o) => !o);
              setFiltroVinculoAberto(false);
            }}
            title="Filtrar por segmento NPS"
            style={{
              display: "flex",
              alignItems: "center",
              gap: 6,
              maxWidth: 260,
              background: filtroNpsAberto
                ? C.greenBg
                : npsSegmentoAtivo
                  ? C.greenBg
                  : C.surf3,
              border: `1px solid ${filtroNpsAberto || npsSegmentoAtivo ? C.g700 : C.border}`,
              borderRadius: 10,
              padding: "6px 12px",
              cursor: "pointer",
              fontSize: 12,
              fontWeight: 500,
              color: filtroNpsAberto || npsSegmentoAtivo ? C.g700 : C.text2,
              transition: "all .15s",
              fontFamily: "inherit",
            }}
          >
            <Crosshair size={13} style={{ flexShrink: 0 }} />
            <span
              style={{
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
                minWidth: 0,
              }}
            >
              {npsSegmentoAtivo || "Todos o NPS"}
            </span>
            <ChevronDown
              size={12}
              style={{
                flexShrink: 0,
                transform: filtroNpsAberto ? "rotate(180deg)" : "none",
                transition: "transform .15s",
              }}
            />
          </button>
          {filtroNpsAberto && (
            <>
              <div
                onClick={() => setFiltroNpsAberto(false)}
                style={{ position: "fixed", inset: 0, zIndex: 199 }}
              />
              <div
                style={{
                  position: "absolute",
                  top: "calc(100% + 8px)",
                  right: 0,
                  minWidth: 240,
                  background: C.surface,
                  border: `1px solid ${C.border}`,
                  borderRadius: 12,
                  boxShadow: "0 12px 32px rgba(0,0,0,.16)",
                  zIndex: 200,
                  overflow: "hidden",
                }}
              >
                <div
                  style={{
                    padding: "12px 14px",
                    borderBottom: `1px solid ${C.border}`,
                    background: C.surf3,
                  }}
                >
                  <div
                    style={{
                      fontSize: 11,
                      fontWeight: 700,
                      color: C.text,
                      textTransform: "uppercase",
                      letterSpacing: "0.05em",
                    }}
                  >
                    Segmento NPS
                  </div>
                </div>
                <div
                  style={{
                    maxHeight: 280,
                    overflowY: "auto",
                    padding: "6px 0",
                  }}
                >
                  {[{ label: "Todos o NPS", value: "" }, ...NPS_OPCOES].map(
                    ({ label, value }) => {
                      const active = npsSegmentoAtivo === value;
                      return (
                        <div
                          key={label}
                          onClick={() => {
                            setNpsSegmentoAtivo(value);
                            dispatch(
                              setNpsSegment(
                                value.replace(/\s*\([^)]*\)\s*$/, ""),
                              ),
                            );
                            setFiltroNpsAberto(false);
                            dispatchAllFetches();
                            trackEvent("filter_applied", "nps_segment", {
                              value: value || "todos",
                            });
                          }}
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 10,
                            padding: "9px 14px",
                            cursor: "pointer",
                            background: active ? C.greenBg : "transparent",
                            transition: "background .12s",
                          }}
                          onMouseEnter={(e) => {
                            if (!active)
                              e.currentTarget.style.background = C.surf3;
                          }}
                          onMouseLeave={(e) => {
                            if (!active)
                              e.currentTarget.style.background = "transparent";
                          }}
                        >
                          <div
                            style={{
                              width: 16,
                              height: 16,
                              borderRadius: "50%",
                              border: `1.5px solid ${active ? C.g700 : C.border}`,
                              background: active ? C.g700 : "#FFFFFF",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              flexShrink: 0,
                            }}
                          >
                            {active && (
                              <div
                                style={{
                                  width: 6,
                                  height: 6,
                                  borderRadius: "50%",
                                  background: "#FFFFFF",
                                }}
                              />
                            )}
                          </div>
                          <span
                            style={{
                              fontSize: 13,
                              color: active ? C.g700 : C.text,
                              fontWeight: active ? 600 : 400,
                            }}
                          >
                            {label}
                          </span>
                        </div>
                      );
                    },
                  )}
                </div>
              </div>
            </>
          )}
        </div>

        {/* Filtro Vínculo Unimed */}
        <div style={{ position: "relative" }}>
          <button
            onClick={() => {
              setFiltroVinculoAberto((o) => !o);
              setFiltroNpsAberto(false);
            }}
            title="Filtrar por vínculo Unimed"
            style={{
              display: "flex",
              alignItems: "center",
              gap: 6,
              maxWidth: 220,
              background: filtroVinculoAberto
                ? C.greenBg
                : vinculoAtivo
                  ? C.greenBg
                  : C.surf3,
              border: `1px solid ${filtroVinculoAberto || vinculoAtivo ? C.g700 : C.border}`,
              borderRadius: 10,
              padding: "6px 12px",
              cursor: "pointer",
              fontSize: 12,
              fontWeight: 500,
              color: filtroVinculoAberto || vinculoAtivo ? C.g700 : C.text2,
              transition: "all .15s",
              fontFamily: "inherit",
            }}
          >
            <Users size={13} style={{ flexShrink: 0 }} />
            <span
              style={{
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
                minWidth: 0,
              }}
            >
              {vinculoAtivo || "Todos Beneficiários"}
            </span>
            <ChevronDown
              size={12}
              style={{
                flexShrink: 0,
                transform: filtroVinculoAberto ? "rotate(180deg)" : "none",
                transition: "transform .15s",
              }}
            />
          </button>
          {filtroVinculoAberto && (
            <>
              <div
                onClick={() => setFiltroVinculoAberto(false)}
                style={{ position: "fixed", inset: 0, zIndex: 199 }}
              />
              <div
                style={{
                  position: "absolute",
                  top: "calc(100% + 8px)",
                  right: 0,
                  minWidth: 220,
                  background: C.surface,
                  border: `1px solid ${C.border}`,
                  borderRadius: 12,
                  boxShadow: "0 12px 32px rgba(0,0,0,.16)",
                  zIndex: 200,
                  overflow: "hidden",
                }}
              >
                <div
                  style={{
                    padding: "12px 14px",
                    borderBottom: `1px solid ${C.border}`,
                    background: C.surf3,
                  }}
                >
                  <div
                    style={{
                      fontSize: 11,
                      fontWeight: 700,
                      color: C.text,
                      textTransform: "uppercase",
                      letterSpacing: "0.05em",
                    }}
                  >
                    Vínculo Unimed
                  </div>
                </div>
                <div
                  style={{
                    maxHeight: 280,
                    overflowY: "auto",
                    padding: "6px 0",
                  }}
                >
                  {[
                    { label: "Todos Beneficiários", value: "", apiValue: "" },
                    ...VINCULO_OPCOES,
                  ].map(({ label, value, apiValue }) => {
                    const active = vinculoAtivo === value;
                    return (
                      <div
                        key={label}
                        onClick={() => {
                          setVinculoAtivo(value);
                          dispatch(setClientType(apiValue));
                          setFiltroVinculoAberto(false);
                          dispatchAllFetches();
                          trackEvent("filter_applied", "client_type", {
                            value: apiValue || "todos",
                          });
                        }}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 10,
                          padding: "9px 14px",
                          cursor: "pointer",
                          background: active ? C.greenBg : "transparent",
                          transition: "background .12s",
                        }}
                        onMouseEnter={(e) => {
                          if (!active)
                            e.currentTarget.style.background = C.surf3;
                        }}
                        onMouseLeave={(e) => {
                          if (!active)
                            e.currentTarget.style.background = "transparent";
                        }}
                      >
                        <div
                          style={{
                            width: 16,
                            height: 16,
                            borderRadius: "50%",
                            border: `1.5px solid ${active ? C.g700 : C.border}`,
                            background: active ? C.g700 : "#FFFFFF",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            flexShrink: 0,
                          }}
                        >
                          {active && (
                            <div
                              style={{
                                width: 6,
                                height: 6,
                                borderRadius: "50%",
                                background: "#FFFFFF",
                              }}
                            />
                          )}
                        </div>
                        <span
                          style={{
                            fontSize: 13,
                            color: active ? C.g700 : C.text,
                            fontWeight: active ? 600 : 400,
                          }}
                        >
                          {label}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>
            </>
          )}
        </div>

        <PeriodPillBig
          value={period}
          onChange={(name) => {
            dispatch(setPeriod(name));
            dispatch(setCity("todas"));
            dispatch(setNpsSegment(""));
            dispatch(setClientType(""));
            setCidadesAtivas([]);
            setNpsSegmentoAtivo("");
            setVinculoAtivo("");
            trackEvent("filter_applied", "period", { value: name });
            const found = filters.periods?.find((p) => p.name === name);
            const typeKey = found
              ? (Object.entries(PERIOD_TYPE_BY_KEY).find(
                  ([, v]) => v === found.type,
                )?.[0] ?? "mensal")
              : "mensal";
            setPeriodoG(typeKey);
            dispatchAllFetches();
          }}
        />

        {/* Notificações (mensagens da Hazo.ai pro cliente) */}
        <NotificationCenter ctx={ctx} />

        {isSuperadmin && (
          <Link
            to="/admin/usage"
            title="Estatísticas de uso"
            style={{
              display: "flex",
              alignItems: "center",
              fontSize: 11,
              fontWeight: 600,
              color: C.text3,
              textDecoration: "none",
              marginRight: 4,
            }}
          >
            Uso
          </Link>
        )}
        <div
          style={{
            width: 30,
            minWidth: 30,
            height: 30,
            flexShrink: 0,
            background: C.g700,
            borderRadius: "50%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: 12,
            fontWeight: 700,
            color: "#FFFFFF",
            cursor: "pointer",
            textTransform: "uppercase",
          }}
        >
          {firebaseAuth.currentUser?.email?.[0] ?? ""}
        </div>
        <div>
          <button onClick={handleLogout}>Sair</button>
        </div>
      </div>

      {/* -- Tabs -- */}
      <div
        className="no-print"
        style={{
          background: "rgba(255,255,255,.9)",
          backdropFilter: "blur(10px)",
          borderBottom: `1px solid ${C.border}`,
          padding: "0 24px",
          display: "flex",
          alignItems: "center",
          gap: 14,
          overflowX: "auto",
        }}
      >
        {/* Grupo 1 — Tabs do relatório */}
        <div
          style={{
            display: "flex",
            background: C.surf3,
            borderRadius: 10,
            padding: 3,
            gap: 1,
            margin: "7px 0",
          }}
        >
          {TABS.map(({ id, label, Icon }) => (
            <button
              key={id}
              onClick={() => handleTabChange(id)}
              style={btnStyle(tab === id)}
            >
              <Icon size={12} />
              {label}
            </button>
          ))}
        </div>
        {/* Grupo 2 — Tabs operacionais (Plano de Ação + CooperAção), mesmo layout, visualmente sombreadas */}
        <div
          style={{
            display: "flex",
            background: "linear-gradient(135deg, #EEF2F7 0%, #E2E8F0 100%)",
            border: `1px dashed ${C.border2 || "#CBD5E1"}`,
            borderRadius: 10,
            padding: 3,
            gap: 1,
            margin: "7px 0",
            boxShadow: "inset 0 1px 2px rgba(15,23,42,.06)",
          }}
        >
          {TABS_OPS.map(({ id, label, Icon }) => (
            <button
              key={id}
              onClick={() => handleTabChange(id)}
              style={{
                ...btnStyle(tab === id),
                background: tab === id ? C.surface : "transparent",
                color: tab === id ? C.text : C.text3,
                opacity: tab === id ? 1 : 0.85,
              }}
            >
              <Icon size={12} />
              {label}
            </button>
          ))}
        </div>
        <div style={{ flex: 1 }} />
      </div>

      <div style={{ padding: "20px 24px", maxWidth: 1360, margin: "0 auto" }}>
        <Overview per={per} metas={metas} />

        {tab === "executivo" && <ExecutiveView ctx={ctx} />}
        {tab === "jornada" && <JourneyView ctx={ctx} />}
        {tab === "tendencias" && <TrendsView ctx={ctx} />}
        {tab === "segmentos" && <SegmentationView ctx={ctx} />}
        {tab === "drivers" && <DriversView ctx={ctx} />}
        {tab === "eva" && <EvaView ctx={ctx} />}
        {tab === "benchmark" && <BenchmarkingView ctx={ctx} />}
        {tab === "acoes" && <ActionPlanView ctx={ctx} />}
        {tab === "coop" && <CooperacaoView ctx={ctx} />}
        {tab === "quest" && <VerbatinsView ctx={ctx} />}
      </div>

      {/* Wizard de Conclusão — disparado quando um plano transita para "concluido" sem sucesso registrado */}
      {concluirWizard &&
        (() => {
          const w = concluirWizard;
          const ganhoNum = Number(w.ganhoEdit) || 0;
          const isPositivo = w.ganhoAuto > 0;
          const closeWizard = () => setConcluirWizard(null);
          const registrarSucessoStep = () => {
            // Atualiza a ação com sucesso registrado
            const acaoAtualizada = {
              ...w.acao,
              sucesso: {
                ganhoPp: ganhoNum,
                observacao: w.obs,
                registradoEm: todayStr(),
              },
            };
            // Calcula badges recém-desbloqueadas comparando antes/depois
            const newAcoes = acoes.some((x) => x.id === acaoAtualizada.id)
              ? acoes.map((x) =>
                  x.id === acaoAtualizada.id ? acaoAtualizada : x,
                )
              : [...acoes, acaoAtualizada];
            const oldUnlocked = getAchievementStats(
              acoes,
              coopPlanos,
              coopCurtidas,
            ).unlockedBadges;
            const newUnlocked = getAchievementStats(
              newAcoes,
              coopPlanos,
              coopCurtidas,
            ).unlockedBadges;
            const newBadges = newUnlocked.filter(
              (b) => !oldUnlocked.some((ob) => ob.id === b.id),
            );

            setAcoes((prev) => {
              const exists = prev.some((x) => x.id === acaoAtualizada.id);
              return exists
                ? prev.map((x) =>
                    x.id === acaoAtualizada.id ? acaoAtualizada : x,
                  )
                : [...prev, acaoAtualizada];
            });
            // Avança pra step de publicação (ganho positivo) ou badge (badges novas) ou fecha
            if (ganhoNum > 0) {
              setConcluirWizard({
                ...w,
                step: "publicar",
                acao: acaoAtualizada,
                newBadges,
              });
            } else if (newBadges.length > 0) {
              setConcluirWizard({
                ...w,
                step: "badge",
                acao: acaoAtualizada,
                newBadges,
              });
            } else {
              setConcluirWizard(null);
            }
          };
          const publicarStep = () => {
            // Publica no CooperAção com os campos editados
            compartilharPlano(w.acao, {
              autor: w.autorPub,
              titulo: w.tituloPub,
              desc: w.descPub,
              telefone: w.telefonePub ? w.telefonePub.trim() : null,
              email: w.emailPub ? w.emailPub.trim() : null,
              silent: true,
            });
            setConcluirWizard(null);
          };

          return (
            <div
              style={{
                position: "fixed",
                inset: 0,
                background: "rgba(0,0,0,.5)",
                backdropFilter: "blur(8px)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                zIndex: 400,
                padding: 20,
              }}
            >
              <div
                style={{
                  background: C.surface,
                  borderRadius: 20,
                  width: "100%",
                  maxWidth: 580,
                  maxHeight: "92vh",
                  overflowY: "auto",
                  boxShadow: "0 32px 80px rgba(0,0,0,.3)",
                  position: "relative",
                }}
              >
                {/* Header do wizard — muda por step */}
                {w.step === "registrar_sucesso" && (
                  <>
                    <div
                      style={{
                        padding: "22px 26px",
                        background: `linear-gradient(135deg,${C.greenBg} 0%,#ECFDF5 100%)`,
                        borderBottom: `1px solid ${C.g300}`,
                        borderRadius: "20px 20px 0 0",
                        display: "flex",
                        alignItems: "center",
                        gap: 14,
                      }}
                    >
                      <div
                        style={{
                          width: 52,
                          height: 52,
                          borderRadius: 14,
                          background: C.green,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          flexShrink: 0,
                          boxShadow: "0 4px 12px rgba(22,163,74,.3)",
                        }}
                      >
                        <Trophy size={24} color="#FFFFFF" />
                      </div>
                      <div style={{ flex: 1 }}>
                        <div
                          style={{
                            fontSize: 10,
                            fontWeight: 700,
                            color: C.green,
                            textTransform: "uppercase",
                            letterSpacing: "0.08em",
                          }}
                        >
                          Plano concluído · etapa 1 de 2
                        </div>
                        <div
                          style={{
                            fontSize: 17,
                            fontWeight: 700,
                            color: C.text,
                            letterSpacing: "-0.02em",
                            marginTop: 2,
                          }}
                        >
                          Parabéns! Vamos registrar o sucesso?
                        </div>
                      </div>
                      <button
                        onClick={closeWizard}
                        style={{
                          background: "rgba(255,255,255,.7)",
                          border: `1px solid ${C.border}`,
                          borderRadius: 10,
                          width: 32,
                          height: 32,
                          cursor: "pointer",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          color: C.text3,
                          fontFamily: "inherit",
                        }}
                      >
                        <X size={16} />
                      </button>
                    </div>
                    <div
                      style={{
                        padding: "22px 26px",
                        display: "flex",
                        flexDirection: "column",
                        gap: 16,
                      }}
                    >
                      {/* Card com o delta calculado */}
                      <div
                        style={{
                          background: C.surf3,
                          borderRadius: 14,
                          padding: "16px 18px",
                          border: `1.5px solid ${
                            isPositivo ? C.green : C.border
                          }`,
                        }}
                      >
                        <div
                          style={{
                            fontSize: 10,
                            fontWeight: 700,
                            color: C.text4,
                            textTransform: "uppercase",
                            letterSpacing: "0.06em",
                            marginBottom: 8,
                          }}
                        >
                          {isPositivo
                            ? "🎯 Impacto detectado automaticamente"
                            : "⚠️ Nenhuma melhoria detectada"}
                        </div>
                        <div
                          style={{
                            display: "grid",
                            gridTemplateColumns: "1fr auto 1fr",
                            gap: 14,
                            alignItems: "center",
                            marginBottom: 10,
                          }}
                        >
                          <div
                            style={{
                              textAlign: "center",
                              padding: "10px 12px",
                              background: C.surface,
                              borderRadius: 10,
                              border: `1px solid ${C.border}`,
                            }}
                          >
                            <div
                              style={{
                                fontSize: 9,
                                color: C.text4,
                                fontWeight: 600,
                                textTransform: "uppercase",
                                letterSpacing: "0.04em",
                              }}
                            >
                              Ao criar o plano
                            </div>
                            <div
                              style={{
                                fontSize: 22,
                                fontWeight: 700,
                                color: C.text3,
                                letterSpacing: "-0.03em",
                                marginTop: 2,
                              }}
                            >
                              {Math.round(w.acao.v_baseline)}
                              {w.acao.etapa === "NPS Geral" ? "" : "%"}
                            </div>
                          </div>
                          <div style={{ fontSize: 20, color: C.text4 }}>→</div>
                          <div
                            style={{
                              textAlign: "center",
                              padding: "10px 12px",
                              background: isPositivo ? C.greenBg : C.surface,
                              borderRadius: 10,
                              border: `1px solid ${
                                isPositivo ? C.green : C.border
                              }`,
                            }}
                          >
                            <div
                              style={{
                                fontSize: 9,
                                color: C.text4,
                                fontWeight: 600,
                                textTransform: "uppercase",
                                letterSpacing: "0.04em",
                              }}
                            >
                              Agora (concluído)
                            </div>
                            <div
                              style={{
                                fontSize: 22,
                                fontWeight: 700,
                                color: isPositivo ? C.green : C.text,
                                letterSpacing: "-0.03em",
                                marginTop: 2,
                              }}
                            >
                              {Math.round(w.acao.v_atual)}
                              {w.acao.etapa === "NPS Geral" ? "" : "%"}
                            </div>
                          </div>
                        </div>
                        <div
                          style={{
                            textAlign: "center",
                            padding: "8px 12px",
                            background: isPositivo ? C.green : C.surf3,
                            color: isPositivo ? "#FFFFFF" : C.text3,
                            borderRadius: 10,
                            fontSize: 14,
                            fontWeight: 700,
                            letterSpacing: "-0.02em",
                          }}
                        >
                          {isPositivo
                            ? `✨ Evolução: +${w.ganhoAuto}${
                                w.acao.etapa === "NPS Geral" ? " pts" : "pp"
                              }`
                            : "Não houve melhoria mensurável neste período"}
                        </div>
                      </div>
                      {/* Input editável do ganho + obs */}
                      <div
                        style={{
                          display: "grid",
                          gridTemplateColumns: "1fr 3fr",
                          gap: 10,
                        }}
                      >
                        <div>
                          <div
                            style={{
                              fontSize: 10,
                              fontWeight: 700,
                              color: C.text3,
                              marginBottom: 4,
                              textTransform: "uppercase",
                              letterSpacing: "0.04em",
                            }}
                          >
                            Ganho (pp)
                          </div>
                          <input
                            type="number"
                            value={w.ganhoEdit}
                            step="1"
                            onChange={(e) =>
                              setConcluirWizard({
                                ...w,
                                ganhoEdit: e.target.value
                                  ? String(Math.round(Number(e.target.value)))
                                  : e.target.value,
                              })
                            }
                            style={{
                              width: "100%",
                              border: `1.5px solid ${C.g300}`,
                              borderRadius: 10,
                              padding: "10px 12px",
                              fontSize: 18,
                              fontWeight: 700,
                              color: C.green,
                              outline: "none",
                              background: C.surface,
                              fontFamily: "inherit",
                              boxSizing: "border-box",
                            }}
                          />
                        </div>
                        <div>
                          <div
                            style={{
                              fontSize: 10,
                              fontWeight: 700,
                              color: C.text3,
                              marginBottom: 4,
                              textTransform: "uppercase",
                              letterSpacing: "0.04em",
                            }}
                          >
                            Observação sobre o resultado (opcional)
                          </div>
                          <input
                            value={w.obs}
                            onChange={(e) =>
                              setConcluirWizard({ ...w, obs: e.target.value })
                            }
                            placeholder="Como vocês sabem que funcionou? O que foi mais impactante?"
                            style={{
                              width: "100%",
                              border: `1.5px solid ${C.border}`,
                              borderRadius: 10,
                              padding: "10px 12px",
                              fontSize: 12,
                              color: C.text,
                              outline: "none",
                              background: C.surface,
                              fontFamily: "inherit",
                              boxSizing: "border-box",
                            }}
                          />
                        </div>
                      </div>
                      {/* Botões de ação */}
                      <div style={{ display: "flex", gap: 10, marginTop: 4 }}>
                        <button
                          onClick={closeWizard}
                          style={{
                            flex: 1,
                            background: C.surf3,
                            color: C.text3,
                            border: `1px solid ${C.border}`,
                            borderRadius: 12,
                            padding: "11px 0",
                            cursor: "pointer",
                            fontSize: 13,
                            fontWeight: 600,
                            fontFamily: "inherit",
                          }}
                        >
                          Agora não
                        </button>
                        <button
                          onClick={registrarSucessoStep}
                          style={{
                            flex: 2,
                            background: C.green,
                            color: "#FFFFFF",
                            border: "none",
                            borderRadius: 12,
                            padding: "11px 0",
                            cursor: "pointer",
                            fontSize: 13,
                            fontWeight: 700,
                            fontFamily: "inherit",
                            boxShadow: "0 4px 12px rgba(22,163,74,.3)",
                          }}
                        >
                          🏆 Registrar sucesso{isPositivo ? " e continuar" : ""}
                        </button>
                      </div>
                    </div>
                  </>
                )}

                {w.step === "publicar" && (
                  <>
                    <div
                      style={{
                        padding: "22px 26px",
                        background: `linear-gradient(135deg,${C.g800} 0%,${
                          C.p700 || C.p500
                        } 100%)`,
                        borderBottom: "none",
                        borderRadius: "20px 20px 0 0",
                        display: "flex",
                        alignItems: "center",
                        gap: 14,
                      }}
                    >
                      <div
                        style={{
                          width: 52,
                          height: 52,
                          borderRadius: 14,
                          background: "rgba(255,255,255,.15)",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          flexShrink: 0,
                        }}
                      >
                        <Share2 size={24} color="#FFFFFF" />
                      </div>
                      <div style={{ flex: 1 }}>
                        <div
                          style={{
                            fontSize: 10,
                            fontWeight: 700,
                            color: "rgba(255,255,255,.7)",
                            textTransform: "uppercase",
                            letterSpacing: "0.08em",
                          }}
                        >
                          Sucesso registrado · etapa 2 de 2
                        </div>
                        <div
                          style={{
                            fontSize: 17,
                            fontWeight: 700,
                            color: "#FFFFFF",
                            letterSpacing: "-0.02em",
                            marginTop: 2,
                          }}
                        >
                          Compartilhe esse aprendizado!
                        </div>
                      </div>
                      <button
                        onClick={closeWizard}
                        style={{
                          background: "rgba(255,255,255,.15)",
                          border: "none",
                          borderRadius: 10,
                          width: 32,
                          height: 32,
                          cursor: "pointer",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          color: "#FFFFFF",
                          fontFamily: "inherit",
                        }}
                      >
                        <X size={16} />
                      </button>
                    </div>
                    <div
                      style={{
                        padding: "22px 26px",
                        display: "flex",
                        flexDirection: "column",
                        gap: 14,
                      }}
                    >
                      {/* Mensagem de incentivo */}
                      <div
                        style={{
                          background: `linear-gradient(135deg,${
                            C.purpleBg || "#F0EDFF"
                          } 0%,${C.surface} 100%)`,
                          borderRadius: 14,
                          padding: "16px 18px",
                          border: `1.5px solid ${C.purple || "#7C68EE"}40`,
                        }}
                      >
                        <div
                          style={{
                            display: "flex",
                            gap: 10,
                            alignItems: "flex-start",
                          }}
                        >
                          <div
                            style={{
                              fontSize: 26,
                              flexShrink: 0,
                              lineHeight: 1,
                            }}
                          >
                            🤝
                          </div>
                          <div>
                            <div
                              style={{
                                fontSize: 13,
                                fontWeight: 700,
                                color: C.text,
                                marginBottom: 4,
                                letterSpacing: "-0.01em",
                              }}
                            >
                              Outras Unimeds podem aprender com você
                            </div>
                            <div
                              style={{
                                fontSize: 11,
                                color: C.text2,
                                lineHeight: 1.55,
                              }}
                            >
                              Seu plano gerou{" "}
                              <b style={{ color: C.green }}>
                                +{ganhoNum}
                                {w.acao.etapa === "NPS Geral" ? " pts" : "pp"}
                              </b>{" "}
                              de impacto em <b>{w.acao.etapa}</b>. Ao publicar
                              no CooperAção Unimed, você ajuda cooperativas que
                              enfrentam o mesmo desafio e fortalece todo o
                              Sistema Unimed.{" "}
                              <b>
                                O cooperativismo é a força de quem compartilha.
                              </b>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Campos editáveis da publicação */}
                      <div>
                        <div
                          style={{
                            fontSize: 10,
                            fontWeight: 700,
                            color: C.text3,
                            marginBottom: 4,
                            textTransform: "uppercase",
                            letterSpacing: "0.04em",
                          }}
                        >
                          Responsável pela publicação
                        </div>
                        <input
                          value={w.autorPub}
                          onChange={(e) =>
                            setConcluirWizard({
                              ...w,
                              autorPub: e.target.value,
                            })
                          }
                          placeholder="Ex: Maria Silva · Gerência CX"
                          style={{
                            width: "100%",
                            border: `1.5px solid ${C.border}`,
                            borderRadius: 10,
                            padding: "10px 12px",
                            fontSize: 13,
                            color: C.text,
                            outline: "none",
                            background: C.surface,
                            fontFamily: "inherit",
                            boxSizing: "border-box",
                          }}
                        />
                        <div
                          style={{
                            fontSize: 9,
                            color: C.text4,
                            marginTop: 3,
                            fontStyle: "italic",
                          }}
                        >
                          Esse nome aparecerá como autor do plano no feed
                          CooperAção. Você pode atribuir a quem liderou a
                          execução.
                        </div>
                      </div>
                      <div>
                        <div
                          style={{
                            fontSize: 10,
                            fontWeight: 700,
                            color: C.text3,
                            marginBottom: 4,
                            textTransform: "uppercase",
                            letterSpacing: "0.04em",
                          }}
                        >
                          Título da publicação
                        </div>
                        <input
                          value={w.tituloPub}
                          onChange={(e) =>
                            setConcluirWizard({
                              ...w,
                              tituloPub: e.target.value,
                            })
                          }
                          style={{
                            width: "100%",
                            border: `1.5px solid ${C.border}`,
                            borderRadius: 10,
                            padding: "10px 12px",
                            fontSize: 13,
                            fontWeight: 600,
                            color: C.text,
                            outline: "none",
                            background: C.surface,
                            fontFamily: "inherit",
                            boxSizing: "border-box",
                          }}
                        />
                      </div>
                      <div>
                        <div
                          style={{
                            fontSize: 10,
                            fontWeight: 700,
                            color: C.text3,
                            marginBottom: 4,
                            textTransform: "uppercase",
                            letterSpacing: "0.04em",
                          }}
                        >
                          Descrição (o que outras Unimeds precisam saber)
                        </div>
                        <textarea
                          value={w.descPub}
                          onChange={(e) =>
                            setConcluirWizard({ ...w, descPub: e.target.value })
                          }
                          rows={3}
                          placeholder="Conte como foi a execução, o que funcionou, quais desafios apareceram..."
                          style={{
                            width: "100%",
                            border: `1.5px solid ${C.border}`,
                            borderRadius: 10,
                            padding: "10px 12px",
                            fontSize: 12,
                            color: C.text,
                            outline: "none",
                            background: C.surface,
                            fontFamily: "inherit",
                            boxSizing: "border-box",
                            resize: "vertical",
                          }}
                        />
                      </div>

                      {/* Contato opcional — telefone/email pra outras Unimeds entrarem em contato */}
                      <div>
                        <div
                          style={{
                            fontSize: 10,
                            fontWeight: 700,
                            color: C.text3,
                            marginBottom: 4,
                            textTransform: "uppercase",
                            letterSpacing: "0.04em",
                            display: "flex",
                            alignItems: "center",
                            gap: 6,
                          }}
                        >
                          Contato para outras Unimeds{" "}
                          <span
                            style={{
                              fontSize: 9,
                              fontWeight: 500,
                              color: C.text4,
                              textTransform: "none",
                              letterSpacing: "normal",
                              fontStyle: "italic",
                            }}
                          >
                            (opcional — só preencha se quiser ser contatado)
                          </span>
                        </div>
                        <div
                          style={{
                            display: "grid",
                            gridTemplateColumns: "1fr 1fr",
                            gap: 8,
                          }}
                        >
                          <div style={{ position: "relative" }}>
                            <Phone
                              size={12}
                              color={C.text4}
                              style={{
                                position: "absolute",
                                left: 11,
                                top: "50%",
                                transform: "translateY(-50%)",
                                pointerEvents: "none",
                              }}
                            />
                            <input
                              type="tel"
                              value={w.telefonePub}
                              onChange={(e) =>
                                setConcluirWizard({
                                  ...w,
                                  telefonePub: e.target.value,
                                })
                              }
                              placeholder="(19) 99999-0000"
                              style={{
                                width: "100%",
                                border: `1.5px solid ${C.border}`,
                                borderRadius: 10,
                                padding: "10px 12px 10px 32px",
                                fontSize: 12,
                                color: C.text,
                                outline: "none",
                                background: C.surface,
                                fontFamily: "inherit",
                                boxSizing: "border-box",
                              }}
                            />
                          </div>
                          <div style={{ position: "relative" }}>
                            <Mail
                              size={12}
                              color={C.text4}
                              style={{
                                position: "absolute",
                                left: 11,
                                top: "50%",
                                transform: "translateY(-50%)",
                                pointerEvents: "none",
                              }}
                            />
                            <input
                              type="email"
                              value={w.emailPub}
                              onChange={(e) =>
                                setConcluirWizard({
                                  ...w,
                                  emailPub: e.target.value,
                                })
                              }
                              placeholder="cx@unimed.com.br"
                              style={{
                                width: "100%",
                                border: `1.5px solid ${C.border}`,
                                borderRadius: 10,
                                padding: "10px 12px 10px 32px",
                                fontSize: 12,
                                color: C.text,
                                outline: "none",
                                background: C.surface,
                                fontFamily: "inherit",
                                boxSizing: "border-box",
                              }}
                            />
                          </div>
                        </div>
                        <div
                          style={{
                            fontSize: 9,
                            color: C.text4,
                            marginTop: 4,
                            fontStyle: "italic",
                            display: "flex",
                            alignItems: "flex-start",
                            gap: 4,
                          }}
                        >
                          <span>ⓘ</span>
                          <span>
                            Cooperativas que quiserem replicar o plano poderão
                            entrar em contato direto. Os dados ficam visíveis no
                            card do feed CooperAção. Deixe em branco para
                            publicar anonimamente.
                          </span>
                        </div>
                      </div>

                      {/* Preview dos drivers que serão publicados */}
                      {(w.acao.drivers || []).length > 0 && (
                        <div>
                          <div
                            style={{
                              fontSize: 10,
                              fontWeight: 700,
                              color: C.text3,
                              marginBottom: 5,
                              textTransform: "uppercase",
                              letterSpacing: "0.04em",
                            }}
                          >
                            Drivers afetados (serão incluídos na publicação)
                          </div>
                          <div
                            style={{
                              display: "flex",
                              flexWrap: "wrap",
                              gap: 5,
                            }}
                          >
                            {(w.acao.drivers || []).map((slug) => {
                              const drv = null;
                              if (!drv) return null;
                              return (
                                <div
                                  key={slug}
                                  style={{
                                    padding: "4px 10px",
                                    borderRadius: 20,
                                    background: C.purpleBg || "#F0EDFF",
                                    border: `1px solid ${
                                      C.purple || "#7C68EE"
                                    }40`,
                                    fontSize: 10,
                                    fontWeight: 600,
                                    color: C.purple || "#7C68EE",
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 4,
                                  }}
                                >
                                  <span style={{ fontSize: 12 }}>
                                    {drv.icon}
                                  </span>
                                  {drv.label}
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}

                      {/* Novas medalhas desbloqueadas junto com a publicação */}
                      {(w.newBadges || []).length > 0 && (
                        <div
                          style={{
                            background: "#FFFBEB",
                            border: "1.5px solid #F59E0B",
                            borderRadius: 12,
                            padding: "10px 14px",
                            marginBottom: 14,
                          }}
                        >
                          <div
                            style={{
                              fontSize: 11,
                              fontWeight: 700,
                              color: "#92400E",
                              marginBottom: 8,
                              textTransform: "uppercase",
                              letterSpacing: 0.5,
                            }}
                          >
                            🏅 Nova{(w.newBadges || []).length > 1 ? "s" : ""}{" "}
                            medalha{(w.newBadges || []).length > 1 ? "s" : ""}{" "}
                            desbloqueada
                            {(w.newBadges || []).length > 1 ? "s" : ""}
                          </div>
                          <div
                            style={{
                              display: "flex",
                              gap: 8,
                              flexWrap: "wrap",
                            }}
                          >
                            {(w.newBadges || []).map((badge) => (
                              <div
                                key={badge.id}
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 6,
                                  background: "#FEF3C7",
                                  borderRadius: 8,
                                  padding: "5px 10px",
                                  fontSize: 12,
                                  fontWeight: 600,
                                  color: "#78350F",
                                }}
                              >
                                <span style={{ fontSize: 16 }}>
                                  {badge.icon}
                                </span>
                                {badge.name}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Botões de ação */}
                      <div style={{ display: "flex", gap: 10, marginTop: 4 }}>
                        <button
                          onClick={closeWizard}
                          style={{
                            flex: 1,
                            background: C.surf3,
                            color: C.text3,
                            border: `1px solid ${C.border}`,
                            borderRadius: 12,
                            padding: "11px 0",
                            cursor: "pointer",
                            fontSize: 13,
                            fontWeight: 600,
                            fontFamily: "inherit",
                          }}
                        >
                          Agora não
                        </button>
                        <button
                          onClick={publicarStep}
                          style={{
                            flex: 2,
                            background: C.green,
                            color: "#FFFFFF",
                            border: "none",
                            borderRadius: 12,
                            padding: "11px 0",
                            cursor: "pointer",
                            fontSize: 13,
                            fontWeight: 700,
                            fontFamily: "inherit",
                            boxShadow: "none",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            gap: 6,
                          }}
                        >
                          <Share2 size={14} /> Publicar no CooperAção
                        </button>
                      </div>
                    </div>
                  </>
                )}

                {w.step === "badge" && (
                  <>
                    {/* Header */}
                    <div
                      style={{
                        padding: "22px 26px",
                        background: `linear-gradient(135deg, #F59E0B 0%, #EF4444 100%)`,
                        borderRadius: "20px 20px 0 0",
                        textAlign: "center",
                      }}
                    >
                      <div style={{ fontSize: 40, marginBottom: 6 }}>🏅</div>
                      <div
                        style={{
                          fontSize: 18,
                          fontWeight: 700,
                          color: "#FFFFFF",
                          fontFamily: F.display,
                        }}
                      >
                        Nova{(w.newBadges || []).length > 1 ? "s" : ""}{" "}
                        conquista{(w.newBadges || []).length > 1 ? "s" : ""}{" "}
                        desbloqueada{(w.newBadges || []).length > 1 ? "s" : ""}!
                      </div>
                      <div
                        style={{
                          fontSize: 13,
                          color: "rgba(255,255,255,0.85)",
                          marginTop: 4,
                          fontFamily: F.text,
                        }}
                      >
                        Continue registrando sucessos para desbloquear mais
                        medalhas
                      </div>
                    </div>

                    {/* Badge list */}
                    <div style={{ padding: "24px 26px" }}>
                      <div
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          gap: 12,
                          marginBottom: 24,
                        }}
                      >
                        {(w.newBadges || []).map((badge) => (
                          <div
                            key={badge.id}
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: 16,
                              background: "#FFFBEB",
                              border: "2px solid #F59E0B",
                              borderRadius: 14,
                              padding: "14px 18px",
                            }}
                          >
                            <div
                              style={{
                                fontSize: 36,
                                lineHeight: 1,
                                flexShrink: 0,
                              }}
                            >
                              {badge.icon}
                            </div>
                            <div>
                              <div
                                style={{
                                  fontSize: 15,
                                  fontWeight: 700,
                                  color: C.text,
                                  fontFamily: F.display,
                                }}
                              >
                                {badge.name}
                              </div>
                              {badge.desc && (
                                <div
                                  style={{
                                    fontSize: 12,
                                    color: C.text3,
                                    marginTop: 2,
                                    fontFamily: F.text,
                                  }}
                                >
                                  {badge.desc}
                                </div>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>

                      <button
                        onClick={closeWizard}
                        style={{
                          width: "100%",
                          background: `linear-gradient(135deg, #F59E0B 0%, #EF4444 100%)`,
                          color: "#FFFFFF",
                          border: "none",
                          borderRadius: 12,
                          padding: "12px 0",
                          cursor: "pointer",
                          fontSize: 14,
                          fontWeight: 700,
                          fontFamily: F.text,
                          boxShadow: "0 4px 14px rgba(245,158,11,0.4)",
                        }}
                      >
                        Incrível! Continuar
                      </button>
                    </div>
                  </>
                )}
              </div>
            </div>
          );
        })()}

      {/* Modal de Plano de Ação — global, abre de qualquer aba (Jornada CTA, Acoes botão, etc) */}
      {modal && modal !== "pdf" && (
        <AcaoModal
          acao={modal === "new" ? null : modal}
          prefilled={modal === "new" ? prefilledAcao : null}
          valoresAtuais={valoresAtuaisModal}
          onSave={(a) => {
            saveAcao(a);
            setPrefilledAcao(null);
          }}
          onClose={() => {
            setModal(null);
            setPrefilledAcao(null);
          }}
        />
      )}

      {/* Modal de engajamento amostral — abre pelo badge no header */}
      {engajamentoModalOpen && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,.55)",
            backdropFilter: "blur(6px)",
            zIndex: 1000,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: 20,
          }}
        >
          <div
            style={{
              background: C.surface,
              borderRadius: 18,
              maxWidth: 780,
              width: "100%",
              maxHeight: "90vh",
              overflow: "auto",
              boxShadow: "0 24px 64px rgba(0,0,0,.3)",
            }}
          >
            {/* Header */}
            <div
              style={{
                padding: "18px 22px",
                borderBottom: `1px solid ${C.border}`,
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                background: `linear-gradient(135deg,${C.greenBg} 0%,${C.surface} 100%)`,
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <div
                  style={{
                    width: 36,
                    height: 36,
                    background: C.g700,
                    borderRadius: 10,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <Activity size={17} color="#FFFFFF" />
                </div>
                <div>
                  <div
                    style={{
                      fontSize: 15,
                      fontWeight: 700,
                      color: C.text,
                      letterSpacing: "-0.02em",
                    }}
                  >
                    Engajamento amostral
                  </div>
                  <div style={{ fontSize: 11, color: C.text4, marginTop: 2 }}>
                    Robustez estatística da pesquisa neste período
                  </div>
                </div>
              </div>
              <button
                onClick={() => setEngajamentoModalOpen(false)}
                title="Fechar"
                style={{
                  background: "transparent",
                  border: "none",
                  cursor: "pointer",
                  padding: 6,
                  borderRadius: 8,
                  color: C.text3,
                  fontFamily: "inherit",
                }}
              >
                <X size={18} />
              </button>
            </div>

            {/* Body */}
            <div style={{ padding: "20px 22px" }}>
              <div
                style={{
                  display: "flex",
                  gap: 10,
                  flexWrap: "wrap",
                  marginBottom: 18,
                }}
              >
                {[
                  {
                    l: "Respondentes",
                    v: overview[0]?.respondents.toLocaleString(),
                    c: C.g700,
                    Icon: UserCheck,
                  },
                  {
                    l: "Ondas no período",
                    v: respondentsByPeriod[pE]?.ondas ?? "—",
                    c: C.p500,
                    Icon: ClipboardList,
                  },
                  {
                    l: "Margem de erro",
                    v: `±${moe(overview[0]?.respondents)}%`,
                    c: moe(overview[0]?.respondents) < 10 ? C.green : C.orange,
                    Icon: Crosshair,
                  },
                  {
                    l: "Nível de confiança",
                    v: "95%",
                    c: C.g600,
                    Icon: CheckCircle2,
                  },
                ].map((k, i) => (
                  <Card
                    key={i}
                    style={{ flex: 1, minWidth: 130, padding: "14px 16px" }}
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 6,
                        marginBottom: 6,
                      }}
                    >
                      <k.Icon size={13} color={k.c} />
                      <div
                        style={{
                          fontSize: 9,
                          color: C.text4,
                          fontWeight: 600,
                          textTransform: "uppercase",
                          letterSpacing: "0.04em",
                        }}
                      >
                        {k.l}
                      </div>
                    </div>
                    <div
                      style={{
                        fontSize: 22,
                        fontWeight: 700,
                        color: k.c,
                        letterSpacing: "-0.03em",
                        fontFamily: F.display,
                      }}
                    >
                      {k.v}
                    </div>
                  </Card>
                ))}
              </div>

              <Section
                title="Respondentes por mês"
                // pad={false}
              >
                <div style={{ padding: "0 20px 16px" }}>
                  <ResponsiveContainer width="100%" height={200}>
                    <BarChart
                      data={respondentsMonthlyChartData}
                      margin={{ top: 8, right: 20 }}
                    >
                      <CartesianGrid
                        strokeDasharray="3 3"
                        stroke={C.border}
                        vertical={false}
                      />
                      <XAxis
                        dataKey="m"
                        tick={{ fontSize: 10, fill: C.text2 }}
                      />
                      <YAxis tick={{ fontSize: 9, fill: C.text4 }} />
                      <Tooltip content={<TT />} />
                      <Bar
                        isAnimationActive
                        dataKey="r"
                        name="Respondentes"
                        fill={C.g600}
                        radius={[4, 4, 0, 0]}
                      />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </Section>

              {/* Recomendação de granularidade — qual período usar como base de análise */}
              {(() => {
                const periodOrder = [
                  "mensal",
                  "trimestral",
                  "semestral",
                  "anual",
                ];
                const periodLabel = {
                  mensal: "Mensal",
                  trimestral: "Trimestral",
                  semestral: "Semestral",
                  anual: "Anual",
                };
                const periodDescr = {
                  mensal:
                    "Decisões em tempo real, ajustes táticos rápidos, acompanhamento de campanhas",
                  trimestral:
                    "Análise estratégica balanceada, comparação T vs T, ciclo de planos de ação",
                  semestral:
                    "Visão consolidada, relatórios executivos, planejamento de médio prazo",
                  anual:
                    "Visão de longo prazo, metas anuais, comparação ano vs ano",
                };
                const hasRespondents = (k) =>
                  Number.isFinite(respondentsByPeriod[k]?.resp) &&
                  respondentsByPeriod[k].resp > 0;
                // A indicação somente se o período for < 10% de margem de erro
                const recKey = periodOrder.find(
                  (k) =>
                    hasRespondents(k) && moe(respondentsByPeriod[k].resp) < 10,
                );
                const isExcelente =
                  recKey && moe(respondentsByPeriod[recKey].resp) <= 3.5;
                const isAceitavel = recKey && !isExcelente;

                // Se houver recomendação (<10%), fica verde. Senão, neutro.
                const recColor = recKey ? C.green : C.text4;

                const recBg = recKey ? C.greenBg : C.surf3;
                const recIconBg = recKey ? C.green : C.text4;
                const recMoe = recKey
                  ? moe(respondentsByPeriod[recKey].resp)
                  : null;
                const recRespondents = recKey
                  ? respondentsByPeriod[recKey].resp.toLocaleString()
                  : "-";

                return (
                  <div style={{ marginTop: 14 }}>
                    <Section title="Recomendação de granularidade">
                      <div
                        style={{
                          fontSize: 11,
                          color: C.text3,
                          marginBottom: 12,
                          lineHeight: 1.6,
                        }}
                      >
                        Com base no volume de respondentes coletados, qual
                        período é estatisticamente confiável para análise:
                      </div>

                      {/* Big recommendation card */}
                      <div
                        style={{
                          padding: "16px 18px",
                          background: recBg,
                          border: `2px solid ${recColor}`,
                          borderRadius: 12,
                          marginBottom: 12,
                        }}
                      >
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 14,
                          }}
                        >
                          <div
                            style={{
                              width: 44,
                              height: 44,
                              background: recIconBg,
                              borderRadius: 12,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              flexShrink: 0,
                              boxShadow: `0 4px 12px ${recColor}40`,
                            }}
                          >
                            <CheckCircle2 size={22} color="#FFFFFF" />
                          </div>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div
                              style={{
                                fontSize: 9,
                                fontWeight: 700,
                                color: recColor,
                                textTransform: "uppercase",
                                letterSpacing: "0.06em",
                                marginBottom: 2,
                              }}
                            >
                              {recKey
                                ? "USE COMO BASE DE ANÁLISE"
                                : "SEM RECOMENDAÇÃO CONFIÁVEL"}
                            </div>
                            <div
                              style={{
                                fontSize: 20,
                                fontWeight: 700,
                                color: C.text,
                                letterSpacing: "-0.02em",
                                lineHeight: 1.1,
                              }}
                            >
                              {recKey
                                ? periodLabel[recKey]
                                : "Nenhum período recomendado"}{" "}
                              <span
                                style={{
                                  fontSize: 13,
                                  fontWeight: 500,
                                  color: recKey ? C.text3 : C.orange,
                                }}
                              >
                                {recKey
                                  ? `· ${recRespondents} respondentes · ±${recMoe}% margem de erro`
                                  : ""}
                              </span>
                            </div>
                            <div
                              style={{
                                fontSize: 11,
                                color: C.text3,
                                marginTop: 4,
                                lineHeight: 1.4,
                              }}
                            >
                              {recKey
                                ? periodDescr[recKey]
                                : "Nenhum período atingiu a margem de erro mínima (<10%) para ser recomendado como base confiável de análise neste momento."}
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Detalhamento por período */}
                      <div
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          gap: 6,
                        }}
                      >
                        {periodOrder.map((k) => {
                          const v = respondentsByPeriod[k];
                          const hasValue = hasRespondents(k);
                          const m = hasValue ? moe(v.resp) : null;
                          const isRec = hasValue && k === recKey;
                          const status = !hasValue
                            ? "Sem dados de respondentes"
                            : m < 10
                              ? "✓ Recomendado"
                              : "✗ Margem de erro elevada — não recomendado";
                          const statusColor = !hasValue
                            ? C.text4
                            : m < 10
                              ? C.green
                              : C.orange;

                          return (
                            <div
                              key={k}
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: 10,
                                padding: "9px 12px",
                                background: isRec ? recBg : C.surf3,
                                border: isRec
                                  ? `1.5px solid ${recColor}`
                                  : `1px solid ${C.border}`,
                                borderRadius: 8,
                              }}
                            >
                              <div style={{ flex: 1, minWidth: 0 }}>
                                <div
                                  style={{
                                    fontSize: 11,
                                    fontWeight: 700,
                                    color: C.text,
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 6,
                                  }}
                                >
                                  {periodLabel[k]}
                                  {isRec && (
                                    <Tag label="recomendado" color={recColor} />
                                  )}
                                </div>
                                <div
                                  style={{
                                    fontSize: 10,
                                    color: statusColor,
                                    marginTop: 2,
                                    fontWeight: 500,
                                  }}
                                >
                                  {status}
                                </div>
                              </div>
                              <div
                                style={{ textAlign: "right", flexShrink: 0 }}
                              >
                                <div
                                  style={{
                                    fontSize: 13,
                                    fontWeight: 700,
                                    color: statusColor,
                                    fontFamily: F.display,
                                    letterSpacing: "-0.02em",
                                  }}
                                >
                                  {hasValue ? `±${m}%` : "-"}
                                </div>
                                <div style={{ fontSize: 9, color: C.text4 }}>
                                  {hasValue
                                    ? `${v.resp.toLocaleString()} resp.`
                                    : "- resp."}
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>

                      <div
                        style={{
                          marginTop: 12,
                          padding: "10px 12px",
                          background: C.surf3,
                          borderRadius: 8,
                          fontSize: 10,
                          color: C.text3,
                          lineHeight: 1.6,
                        }}
                      >
                        <b style={{ color: C.text }}>Como ler:</b> a
                        recomendação é baseada na granularidade mais detalhada
                        cuja amostra sustenta margem de erro confiável (≤5%).
                        Granularidades mais finas podem ser visualizadas no
                        dashboard, mas decisões críticas devem se basear no
                        período recomendado. Períodos marcados como "cautela"
                        podem ser usados como tendência indicativa, sempre
                        acompanhados da margem de erro explícita.
                      </div>
                    </Section>
                  </div>
                );
              })()}

              <div style={{ marginTop: 14 }}>
                <Section title="Margem de erro por período">
                  <div
                    style={{
                      fontSize: 11,
                      color: C.text3,
                      marginBottom: 10,
                      lineHeight: 1.6,
                    }}
                  >
                    Calculada com <b>95%</b> de confiança:&nbsp;
                    <b
                      style={{
                        color: C.text,
                        fontFamily: F.mono,
                        fontSize: 11,
                      }}
                    >
                      ME = 1,96 × √((0,25 / n) × ((N - n) / (N - 1))) × 100
                    </b>
                  </div>
                  <div
                    style={{ display: "flex", flexDirection: "column", gap: 6 }}
                  >
                    {PERIODS.map((k) => {
                      const v = respondentsByPeriod[k];
                      const hasValue = Number.isFinite(v?.resp) && v.resp > 0;
                      const me = hasValue ? moe(v.resp) : null;
                      const color = !hasValue
                        ? C.text4
                        : me < 10
                          ? C.green
                          : C.orange;
                      return (
                        <div
                          key={k}
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 10,
                            padding: "7px 11px",
                            background: C.surf3,
                            borderRadius: 8,
                          }}
                        >
                          <div style={{ flex: 1 }}>
                            <div
                              style={{
                                fontSize: 11,
                                fontWeight: 600,
                                color: C.text,
                              }}
                            >
                              {v.short}
                            </div>
                            <div style={{ fontSize: 9, color: C.text4 }}>
                              {hasValue
                                ? `${v.resp.toLocaleString()} respondentes`
                                : "- respondentes"}
                            </div>
                          </div>
                          <div
                            style={{
                              fontSize: 16,
                              fontWeight: 700,
                              letterSpacing: "-0.03em",
                              color,
                              fontFamily: F.display,
                            }}
                          >
                            {hasValue ? `±${me}%` : "-"}
                          </div>
                          <Tag
                            label={
                              !hasValue
                                ? "Sem dados"
                                : me < 10
                                  ? me <= 3.5
                                    ? "Excelente"
                                    : "Recomendado"
                                  : "Atenção"
                            }
                            color={color}
                          />
                        </div>
                      );
                    })}
                  </div>
                  <div
                    style={{
                      marginTop: 8,
                      padding: "7px 10px",
                      background: C.blueBg,
                      borderRadius: 7,
                      fontSize: 10,
                      color: C.blue,
                    }}
                  >
                    &lt;10% Recomendado · ≥10% Atenção
                  </div>
                </Section>
              </div>
            </div>
          </div>
        </div>
      )}

      {modal === "pdf" && (
        <PDFExportModal
          currentPeriod={period}
          onClose={() => setModal(null)}
          onGenerate={handleGeneratePDF}
        />
      )}

      {printPages &&
        (() => {
          const TYPE_TO_KEY = {
            monthly: "mensal",
            quarterly: "trimestral",
            semiannual: "semestral",
            annual: "anual",
          };
          const printPeriodInfo = storedPeriods.find(
            (p) =>
              p.name === printPeriodo &&
              (!p.city || p.city === "") &&
              (!p.nps_segment || p.nps_segment === "") &&
              (!p.client_type || p.client_type === ""),
          );
          const printPeriodoG = TYPE_TO_KEY[printPeriodInfo?.type] ?? periodoG;
          const printRespondents = overview[0]?.respondents ?? 0;
          const printPer = buildSelectedPer(
            periodRows,
            printPeriodo,
            printRespondents,
          );
          const printCtx = {
            ...ctx,
            periodoG: printPeriodoG,
            per: printPer,
            isPrint: true,
          };
          const printMoe = moe(printRespondents);

          // Reusable page header: section label + logo top-right
          const PdfPageHeader = ({ label }) => (
            <div className="pdf-page-header">
              <span className="pdf-page-header-label">{label}</span>
              <img src={Logo} alt="121" className="pdf-page-header-logo" />
            </div>
          );

          // Engagement stats for the engagement print page
          const engStatCards = [
            {
              l: "Respondentes",
              v: printRespondents.toLocaleString(),
              c: C.g700,
            },
            { l: "Ondas no período", v: printPer.ondas ?? "—", c: C.p500 },
            {
              l: "Margem de erro",
              v: `±${printMoe}%`,
              c: printMoe < 10 ? C.green : C.orange,
            },
            { l: "Nível de confiança", v: "95%", c: C.g600 },
          ];

          // Pages that appear after cover in a fixed order (quest first, then engagement, then the rest)
          const CONTENT_PAGE_LABELS = {
            executivo: "Visão Geral",
            jornada: "Jornada CX",
            drivers: "Drivers de Impacto",
            tendencias: "Tendências",
            segmentos_nps: "Segmentação NPS",
            segmentos_csat: "Segmentação CSAT",
            segmentos_ces: "Segmentação CES",
            benchmark: "Benchmarking",
            acoes: "Plano de Ação",
          };

          const viewForId = (id) => {
            if (id === "executivo") return <ExecutiveView ctx={printCtx} />;
            if (id === "jornada") return <JourneyView ctx={printCtx} />;
            if (id === "drivers") return <DriversView ctx={printCtx} />;
            if (id === "tendencias") return <TrendsView ctx={printCtx} />;
            if (id === "segmentos_nps")
              return (
                <SegmentationView ctx={{ ...printCtx, segIndicador: "nps" }} />
              );
            if (id === "segmentos_csat")
              return (
                <SegmentationView ctx={{ ...printCtx, segIndicador: "csat" }} />
              );
            if (id === "segmentos_ces")
              return (
                <SegmentationView ctx={{ ...printCtx, segIndicador: "ces" }} />
              );
            if (id === "benchmark") return <BenchmarkingView ctx={printCtx} />;
            if (id === "acoes") return <ActionPlanView ctx={printCtx} />;
            return null;
          };

          // ── Rodapé — presente em toda página de conteúdo (capa fica de fora) ──
          const TIPO_LABEL = {
            mensal: "Mensal",
            trimestral: "Trimestral",
            semestral: "Semestral",
            anual: "Anual",
          };
          const printGeneratedAt = new Date();
          const geradoEmStr = `${printGeneratedAt.toLocaleDateString("pt-BR")} às ${printGeneratedAt.toLocaleTimeString(
            "pt-BR",
            { hour: "2-digit", minute: "2-digit" },
          )}`;
          // loggedUserName (backend /auth/me → fullName) é a fonte de verdade
          // do nome completo; displayName do Firebase é respaldo pra quando
          // o backend ainda não carregou.
          const geradoPor =
            loggedUserName ||
            firebaseAuth.currentUser?.displayName ||
            firebaseAuth.currentUser?.email ||
            "—";
          // companyName já vem com "Unimed" no nome (ex.: "Unimed Campinas") —
          // não prefixar de novo aqui.
          const filtrosAtivosStr = describeActiveFilters(filters);
          const clientePeriodoStr = `${companyName} · Relatório ${
            TIPO_LABEL[printPeriodoG] ?? printPeriodoG
          } ${printPer.short || printPer.label || ""}${
            filtrosAtivosStr ? ` · ${filtrosAtivosStr}` : ""
          }`;
          const footerLine = `Confidencial · Uso interno · Não distribuir sem autorização · © 2026 121Labs · 121 Insights CX · Todos os direitos reservados · ${clientePeriodoStr} · Gerado em ${geradoEmStr} por ${geradoPor}`;

          // Ordem real de renderização das páginas com rodapé — inclui a capa,
          // o rodapé aparece em todas as páginas sem exceção.
          const footerPageIds = [
            "capa",
            ...(printPages.includes("quest") ? ["quest"] : []),
            "engajamento",
            ...Object.keys(CONTENT_PAGE_LABELS).filter((id) =>
              printPages.includes(id),
            ),
            "legal",
          ];
          const totalFooterPages = footerPageIds.length;
          const pageNumberOf = (id) => footerPageIds.indexOf(id) + 1;

          const PdfPageFooter = ({ pageId }) => (
            <div className="pdf-page-footer">
              <div className="pdf-page-footer-info">{footerLine}</div>
              <div className="pdf-page-footer-num">
                Página {pageNumberOf(pageId)} de {totalFooterPages}
              </div>
            </div>
          );

          // Cada "página lógica" de conteúdo é uma <table> com thead/tfoot: quando
          // o conteúdo é mais alto que uma página física e quebra em duas (ex.: um
          // gráfico grande), o navegador repete o cabeçalho e o rodapé em cada
          // página física — o que uma <div> comum não faz. Importante: essa
          // marcação fica inline em cada ponto de uso (não como um componente
          // definido aqui dentro do render) porque um componente recriado a cada
          // render do Dashboard forçaria remontar toda a subárvore (inclusive os
          // gráficos Recharts) a cada render, causando um loop de "Maximum update
          // depth exceeded".

          // PrintModeContext desliga a animação dos gráficos: o print dispara
          // antes de o Recharts terminar de desenhar as séries (ver
          // charts/printMode.js).
          return (
            <PrintModeContext.Provider value={true}>
              <div id="pdf-print-container">
                {/* ── CAPA ─────────────────────────────────────────────────────── */}
                <div className="print-page print-cover">
                  <div className="cover-bg">
                    <div className="cover-logo-wrap">
                      <img src={Logo} alt="121 CX" className="cover-logo" />
                    </div>
                    <div className="cover-company">{companyName}</div>
                    <div className="cover-title">
                      Relatório de Experiência do Cliente
                    </div>
                    <div className="cover-period">
                      {printPer.label || printPeriodoG}
                    </div>
                    {filtrosAtivosStr && (
                      <div className="cover-filters">{filtrosAtivosStr}</div>
                    )}
                    <div className="cover-footer">
                      <span className="cover-footer-info">{footerLine}</span>
                      <span className="cover-footer-num">
                        Página {pageNumberOf("capa")} de {totalFooterPages}
                      </span>
                    </div>
                  </div>
                </div>

                {/* ── METODOLOGIA — logo após a capa ───────────────────────────── */}
                {printPages.includes("quest") && (
                  <table className="print-page">
                    <thead>
                      <tr>
                        <td>
                          <PdfPageHeader label="Metodologia" />
                        </td>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td>
                          <div className="pdf-body">
                            <VerbatinsView ctx={printCtx} />
                          </div>
                        </td>
                      </tr>
                    </tbody>
                    <tfoot>
                      <tr>
                        <td>
                          <PdfPageFooter pageId="quest" />
                        </td>
                      </tr>
                    </tfoot>
                  </table>
                )}

                {/* ── ENGAJAMENTO ──────────────────────────────────────────────── */}
                <table className="print-page">
                  <thead>
                    <tr>
                      <td>
                        <PdfPageHeader label="Engajamento Amostral" />
                      </td>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td>
                        <div className="pdf-body">
                          <div
                            style={{
                              fontSize: 18,
                              fontWeight: 700,
                              color: C.text,
                              marginBottom: 16,
                              fontFamily: F.display,
                            }}
                          >
                            Robustez estatística da pesquisa
                          </div>
                          <div
                            style={{ display: "flex", gap: 12, marginBottom: 20 }}
                          >
                            {engStatCards.map((k) => (
                              <div
                                key={k.l}
                                style={{
                                  flex: 1,
                                  background: C.surface,
                                  border: `1px solid ${C.border}`,
                                  borderRadius: 12,
                                  padding: "14px 16px",
                                }}
                              >
                                <div
                                  style={{
                                    fontSize: 10,
                                    color: C.text4,
                                    fontWeight: 600,
                                    textTransform: "uppercase",
                                    letterSpacing: "0.04em",
                                    marginBottom: 6,
                                  }}
                                >
                                  {k.l}
                                </div>
                                <div
                                  style={{
                                    fontSize: 26,
                                    fontWeight: 700,
                                    color: k.c,
                                    letterSpacing: "-0.03em",
                                    fontFamily: F.display,
                                  }}
                                >
                                  {k.v}
                                </div>
                              </div>
                            ))}
                          </div>
                          <div
                            style={{
                              background: C.surface,
                              border: `1px solid ${C.border}`,
                              borderRadius: 12,
                              padding: "16px 20px",
                              marginBottom: 16,
                            }}
                          >
                            <div
                              style={{
                                fontSize: 12,
                                fontWeight: 600,
                                color: C.text,
                                marginBottom: 12,
                              }}
                            >
                              Respondentes por mês
                            </div>
                            <ResponsiveContainer width="100%" height={180}>
                              <BarChart
                                data={respondentsMonthlyChartData}
                                margin={{ top: 4, right: 16, bottom: 0, left: 0 }}
                              >
                                <CartesianGrid
                                  strokeDasharray="3 3"
                                  stroke={C.border}
                                  vertical={false}
                                />
                                <XAxis
                                  dataKey="m"
                                  tick={{ fontSize: 10, fill: C.text2 }}
                                />
                                <YAxis tick={{ fontSize: 9, fill: C.text4 }} />
                                <Tooltip content={<TT />} />
                                <Bar
                                  isAnimationActive={false}
                                  dataKey="r"
                                  name="Respondentes"
                                  fill={C.g600}
                                  radius={[4, 4, 0, 0]}
                                />
                              </BarChart>
                            </ResponsiveContainer>
                          </div>
                          <div
                            style={{
                              background: printMoe < 10 ? C.greenBg : C.orangeBg,
                              border: `1.5px solid ${printMoe < 10 ? C.green : C.orange}`,
                              borderRadius: 12,
                              padding: "12px 16px",
                              fontSize: 12,
                              color: C.text2,
                              lineHeight: 1.6,
                            }}
                          >
                            <b>Margem de erro:</b> ±{printMoe}% com 95% de nível
                            de confiança para {printRespondents.toLocaleString()}{" "}
                            respondentes.
                            {printMoe <= 5
                              ? " Amostra excelente para análises estratégicas."
                              : printMoe <= 10
                                ? " Amostra confiável para análise."
                                : " Considere ampliar o período para maior precisão."}
                          </div>
                        </div>
                      </td>
                    </tr>
                  </tbody>
                  <tfoot>
                    <tr>
                      <td>
                        <PdfPageFooter pageId="engajamento" />
                      </td>
                    </tr>
                  </tfoot>
                </table>

                {/* ── PÁGINAS DE CONTEÚDO ──────────────────────────────────────── */}
                {Object.entries(CONTENT_PAGE_LABELS).map(([id, label]) => {
                  if (!printPages.includes(id)) return null;
                  const view = viewForId(id);
                  if (!view) return null;
                  return (
                    <table key={id} className="print-page">
                      <thead>
                        <tr>
                          <td>
                            <PdfPageHeader label={label} />
                          </td>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td>
                            <div className="pdf-body">{view}</div>
                          </td>
                        </tr>
                      </tbody>
                      <tfoot>
                        <tr>
                          <td>
                            <PdfPageFooter pageId={id} />
                          </td>
                        </tr>
                      </tfoot>
                    </table>
                  );
                })}

                {/* ── LGPD & PROPRIEDADE INTELECTUAL — sempre a última página ──── */}
                <table className="print-page">
                  <thead>
                    <tr>
                      <td>
                        <PdfPageHeader label="Informações Legais" />
                      </td>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td>
                        <div className="pdf-body">
                          <div className="legal-section">
                            <div className="legal-section-title">LGPD</div>
                            <p className="legal-section-text">
                              Este relatório contém dados agregados e
                              anonimizados, sem informação pessoal identificável,
                              conforme Art. 5º, XI da LGPD.
                            </p>
                          </div>
                          <div className="legal-section">
                            <div className="legal-section-title">
                              Propriedade intelectual
                            </div>
                            <p className="legal-section-text">
                              Metodologia, drivers canônicos, escalas Top2Box e
                              framework de pilares CX são propriedade intelectual
                              da 121Labs, utilizados sob licença pela operadora
                              contratante conforme contrato vigente.
                            </p>
                          </div>
                        </div>
                      </td>
                    </tr>
                  </tbody>
                  <tfoot>
                    <tr>
                      <td>
                        <PdfPageFooter pageId="legal" />
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </PrintModeContext.Provider>
          );
        })()}

      <style>{`
        @page { size: A4 landscape; margin: 0; }
        /* Cada "página lógica" é uma <table> com thead/tfoot: quando o
           conteúdo do tbody é mais alto que uma página física e quebra em
           mais de uma, o navegador repete o cabeçalho e o rodapé em cada
           página física — uma <div> comum não faz isso. */
        .print-page { width: 100%; border-collapse: collapse; table-layout: fixed; }
        .print-page > thead > tr > td,
        .print-page > tbody > tr > td,
        .print-page > tfoot > tr > td { padding: 0; border: none; }
        @media print {
          /* Force background colors and images to print without browser dialog */
          * { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
          .no-print { display: none !important; }
          #dashboard-root > *:not(#pdf-print-container) { display: none !important; }
          #pdf-print-container { position: static !important; width: auto !important; left: 0 !important; visibility: visible !important; display: block !important; }
          .print-page { page-break-after: always; break-after: page; }
          .print-page:last-child { page-break-after: avoid; break-after: avoid; }
          /* Permite que a linha/célula única do tbody quebre entre páginas
             físicas — é o que faz o thead/tfoot se repetir a cada página. */
          .print-page tr, .print-page td { page-break-inside: auto; break-inside: auto; }
          /* Individual cards stay intact — break between cards, not inside them */
          .compCard { break-inside: avoid; page-break-inside: avoid; }
          .tabJourneyView { break-inside: auto; page-break-inside: auto; }
        }
        @media screen {
          /* Largura = página A4 paisagem (@page margin 0). Os gráficos medem a
             largura aqui, fora da tela, e não remedem na hora do print: com
             1280px o SVG saía mais largo que a página (~1123px) e era cortado
             à direita. */
          #pdf-print-container { position: fixed; left: -9999px; top: 0; width: 297mm; pointer-events: none; z-index: -1; }
          #pdf-print-container, #pdf-print-container * { animation-duration: 0ms !important; animation-delay: 0ms !important; transition-duration: 0ms !important; }
        }
        /* Cover page */
        .print-cover { padding: 0 !important; }
        .cover-bg {
          /* A4 landscape = 297mm × 210mm; margin:0 so fill the whole page */
          height: 210mm;
          background: linear-gradient(145deg, ${C.g900} 0%, ${C.g700} 45%, ${C.p700} 100%);
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          padding: 60px 80px;
          gap: 0;
          text-align: center;
          position: relative;
          box-sizing: border-box;
        }
        .cover-logo-wrap {
          background: rgba(255,255,255,0.15);
          border-radius: 24px;
          padding: 18px 28px;
          margin-bottom: 36px;
        }
        .cover-logo { height: 56px; display: block; }
        .cover-company {
          font-size: 14px;
          font-weight: 600;
          color: rgba(255,255,255,0.65);
          letter-spacing: 0.12em;
          text-transform: uppercase;
          margin-bottom: 14px;
        }
        .cover-title {
          font-size: 36px;
          font-weight: 800;
          color: #FFFFFF;
          letter-spacing: -0.03em;
          line-height: 1.15;
          margin-bottom: 16px;
          font-family: ${F.display};
        }
        .cover-period {
          font-size: 18px;
          font-weight: 500;
          color: rgba(255,255,255,0.8);
          margin-bottom: 0;
        }
        .cover-filters {
          margin-top: 10px;
          font-size: 13px;
          font-weight: 600;
          color: rgba(255,255,255,0.7);
        }
        .cover-footer {
          position: absolute;
          bottom: 20px;
          left: 80px;
          right: 80px;
          display: flex;
          align-items: flex-end;
          justify-content: space-between;
          gap: 12px;
          font-size: 8px;
          color: rgba(255,255,255,0.45);
          letter-spacing: 0.02em;
          line-height: 1.5;
          text-align: left;
        }
        .cover-footer-info { flex: 1; min-width: 0; }
        .cover-footer-num {
          flex-shrink: 0;
          font-size: 9px;
          font-weight: 700;
          color: rgba(255,255,255,0.7);
          white-space: nowrap;
        }
        /* Per-page header bar — purple brand bar with 121 logo */
        .pdf-page-header {
          background: ${C.p700};
          color: #FFFFFF;
          padding: 8px 20px;
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 12px;
        }
        .pdf-page-header-label {
          font-size: 11px;
          font-weight: 700;
          letter-spacing: 0.08em;
          text-transform: uppercase;
          color: rgba(255,255,255,0.92);
          font-family: ${F.display};
        }
        .pdf-page-header-logo {
          height: 24px;
          width: 24px;
          border-radius: 50%;
          flex-shrink: 0;
          /* white ring so lighter-purple circle is visible on dark purple bar */
          outline: 2px solid rgba(255,255,255,0.5);
          outline-offset: 1px;
        }
        /* Content body with padding */
        .pdf-body { padding: 16px 20px; }
        /* Per-page footer bar — legal/tracking info + page count */
        .pdf-page-footer {
          margin-top: 10px;
          padding: 8px 20px 10px;
          border-top: 1px solid ${C.border};
          display: flex;
          justify-content: space-between;
          align-items: flex-end;
          gap: 12px;
        }
        .pdf-page-footer-info {
          flex: 1;
          min-width: 0;
          font-size: 8px;
          color: ${C.text4};
          line-height: 1.5;
        }
        .pdf-page-footer-num {
          flex-shrink: 0;
          font-size: 9px;
          font-weight: 700;
          color: ${C.text3};
          white-space: nowrap;
        }
        /* Legal page — LGPD & propriedade intelectual */
        .legal-section { margin-bottom: 22px; }
        .legal-section-title {
          font-size: 14px;
          font-weight: 700;
          color: ${C.text};
          margin-bottom: 8px;
          font-family: ${F.display};
        }
        .legal-section-text {
          font-size: 12px;
          color: ${C.text2};
          line-height: 1.7;
          margin: 0;
          max-width: 780px;
        }
        * { box-sizing:border-box; }
        button,input,select,textarea { font-family:inherit; }
        ::-webkit-scrollbar { width:5px; height:5px; }
        ::-webkit-scrollbar-track { background:transparent; }
        ::-webkit-scrollbar-thumb { background:${C.border}; border-radius:3px; }
      `}</style>
    </div>
  );
}
